import { router } from "../router/router.js";
import { createIcons, ArrowLeft, CheckCircle, FileText, CheckCircle2, ShieldCheck, PenTool, XCircle, Search, Edit3, Send, AlertTriangle, AlertCircle } from "lucide";
import { contextStore } from "../auth/context-store.js";
import { formatZrDisplay, isPendingZr, ZR_PENDING_LABEL } from "../../domain/recommendation/zr-numbering.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { platform } from "../../app/browser-platform.js";
import type { EngineeringWorkspaceContext, MinutaWorkspaceContext } from "../../application/services/engineering-service.js";
import { AppError } from "../../shared/errors/app-error.js";
import { computeValidateMinutaState } from "./engineering-workspace-minuta-actions.js";
import "../components/document/pdf-preview.js";
import type { EngineeringAssessmentContext, EngineeringNdtRow, FinalizationGate, MinutaTecnicaContext } from "../../application/services/engineering-service.js";
import { EQUIPMENT_CONDITION_LABELS, RESIDUAL_LIFE_MAX_MONTHS, RESIDUAL_LIFE_ORIGIN_LABELS, deriveEquipmentResult, formatResidualLifeMonths, type EquipmentCondition } from "../../domain/report/integrity-assessment.js";
import { CORRECTION_UI_STYLES, openCorrectionRequestModal } from "../components/correction-request-ui.js";
import { PLH_REVIEW_UI_STYLES } from "../components/plh-review-ui.js";
import { ENGINEERING_WORKFLOW_STATUS_LABELS, workflowStatusOfIntegrityReport } from "../../domain/review/engineering-workflow-status.js";
import type { TechnicalCorrectionRequest } from "../../domain/review/technical-correction.js";

export class EngineeringWorkspace extends HTMLElement {
  private reportId: string | null = null;
  private workspaceData: EngineeringWorkspaceContext | null = null;
  private checklistComponents: any[] = [];
  private minutaContext: MinutaWorkspaceContext | null = null;
  private minutaPdfSnapshot: any = null;
  private riPdfSnapshot: any = null;
  private showReturnMinutaModal = false;
  private showEditReportModal = false;
  private nameCache: Map<string, string> = new Map();
  private isMinutaLoading = false;
  private activeCorrectionId: string | null = null;
  private activeRecId: string | null = null;
  private minutaEditMode = false;
  
  // Modals state
  private showStartAnalysisModal = false;
  private showEditRecModal = false;
  private showRequestCorrectionModal = false;
  private showValidateCorrectionModal = false;
  private showCompleteAnalysisModal = false;
  private showApproveSignModal = false;
  private showHistoryModal = false;
  private equipmentTag = "";
  private activeTab: string = "summary";
  /** Painel de Engenharia: cabeçalho macro, avaliação (condição + vida residual), AVR encontrado, correções por item. */
  private assessmentCtx: EngineeringAssessmentContext | null = null;
  private assessmentForm: { condition: EquipmentCondition | ""; months: string; error: string; notice: string; busy: boolean } = { condition: "", months: "", error: "", notice: "", busy: false };
  private riCorrections: readonly TechnicalCorrectionRequest[] = [];
  /** Minuta Técnica (fonte única): diff pela fonte (hash), pendências de validação; FINALIZAR; END do contexto. */
  private minutaTecnica: MinutaTecnicaContext | null = null;
  private minutaNotes = "";
  private minutaPreviewOpen = false;
  private finalizeGate: FinalizationGate | null = null;
  private ndtRows: EngineeringNdtRow[] = [];
  private ndtLoaded = false;

  private mapStatus(status: string): string {
    const map: Record<string, string> = {
      'IN_ELABORATION': 'EM ELABORAÇÃO',
      'SUBMITTED_TO_ENGINEERING': 'AGUARDANDO ANÁLISE',
      'UNDER_ENGINEERING_REVIEW': 'EM ANÁLISE',
      'AWAITING_SIGNATURE': 'AGUARDANDO ASSINATURA',
      'SIGNED': 'ASSINADO',
      'COMPLETED': 'CONCLUÍDO',
      'RESUBMITTED': 'RESSUBMETIDO',
      'WAITING_ANALYSIS': 'AGUARDANDO ANÁLISE',
      'IN_ANALYSIS': 'EM ANÁLISE',
      'WAITING_CORRECTION': 'AGUARDANDO CORREÇÃO',
      'APPROVED': 'APROVADA',
      'CORRECTION_NEEDED': 'CORREÇÃO NECESSÁRIA',
      'AWAITING_INSPECTOR': 'AGUARDANDO INSPETOR'
    };
    return map[status] || status;
  }

  static get observedAttributes() {
    return ["id"];
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (name === "id" && oldValue !== newValue) {
      this.reportId = newValue;
      this.loadWorkspace();
    }
  }

  connectedCallback() {
    this.render();
  }

  /** Contexto de requisição da sessão, como o resto da página monta. */
  private reqCtx(): any {
    const ctx = contextStore.get();
    return {
      activeClientId: ctx.activeClientId || "",
      permissions: ctx.permissions,
      correlationId: "",
      userId: ctx.userId,
      professionalId: ctx.professionalId ?? null
    };
  }

  /** Ações de numeração ZR aparecem só para quem tem a permissão específica. */
  private canEditZrNumber(): boolean {
    return Boolean(contextStore.get()?.permissions?.has(PermissionCode.EngineeringEditZrNumber));
  }

  private canRemoveZrNumber(): boolean {
    return Boolean(contextStore.get()?.permissions?.has(PermissionCode.EngineeringRemoveZrNumber));
  }

  private esc(value: unknown): string {
    return String(value ?? "").replace(/[&<>"']/g, c =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] || c)
    );
  }

  private async loadWorkspace() {
    if (!this.reportId) return;
    try {
      this.innerHTML = `<div style="padding: 24px;">Carregando workspace...</div>`;
      const ctx = contextStore.get();
      const reqContext = { activeClientId: ctx.activeClientId || "", permissions: ctx.permissions, correlationId: "", userId: ctx.userId } as any;
      
      this.workspaceData = await platform.engineeringService.getWorkspaceContext(reqContext, this.reportId as any);

      this.equipmentTag = "";
      const snapshotId = this.workspaceData.report.equipmentSnapshotId;
      if (snapshotId) {
        try {
          const snapshot = await platform.equipmentService.getSnapshot(reqContext, snapshotId);
          const typedTag = snapshot?.equipmentData?.tag;
          if (typedTag) {
            this.equipmentTag = typedTag;
          } else if (snapshot?.payloadJson) {
            const payload: unknown = JSON.parse(snapshot.payloadJson);
            if (typeof payload === "object" && payload !== null && "tag" in payload && typeof payload.tag === "string") {
              this.equipmentTag = payload.tag;
            }
          }
        } catch (snapshotError) {
          console.warn("Could not load equipment snapshot in engineering workspace", snapshotError);
        }
      }
      
      // Load checklist components and their photos
      this.checklistComponents = [];
      if (this.workspaceData?.report?.inspectionId && platform.inspectionService) {
        try {
          const chkState = await platform.inspectionService.getChecklistState(reqContext, this.workspaceData.report.inspectionId as any);
          if (chkState && chkState.components && chkState.components.length > 0) {
            const mappedComps = [];
            for (const c of chkState.components) {
              const compPhotos = (chkState.photos || [])
                .filter((ph: any) => ph.componentId === c.id)
                .map((ph: any) => ({
                  id: ph.id,
                  url: ph.fileUrl || ph.url || ph.image,
                  comment: ph.comment || ''
                }));

              mappedComps.push({
                ...c,
                photos: compPhotos
              });
            }
            this.checklistComponents = mappedComps;
          }
        } catch (err) {
          console.warn("Could not load checklist components in engineering workspace", err);
        }
      }

      await this.loadAssessmentContext();
      // Atalho do Painel (…#minuta): abre direto na Minuta Técnica.
      if (typeof location !== "undefined" && location.hash === "#minuta") this.activeTab = "minuta";
      this.render();

      // Eager-load Minuta context (recommendations/corrections) so both the
      // Checklist & Fotos tab and the Análise da Minuta tab can show
      // engineering status without waiting for the user to visit the Minuta
      // tab first (Bloco 9, Parte C).
      this.loadMinutaContext().catch(() => {});
      this.loadRiPdfSnapshot(reqContext).catch(() => {});
    } catch (e) {
      this.innerHTML = `<div style="padding: 24px; color: red;">Erro ao carregar workspace: ${e instanceof AppError ? e.message : String(e)}</div>`;
    }
  }

  /** Avaliação de Engenharia + AVR + descrição macro + itens de correção do RI. */
  private async loadAssessmentContext() {
    if (!this.reportId) return;
    try {
      this.assessmentCtx = await platform.engineeringService.getAssessmentContext(this.reqCtx(), this.reportId as any);
      const a = this.assessmentCtx.assessment;
      this.assessmentForm = { condition: a.equipmentCondition || "", months: a.residualLife && a.residualLife.origin === "ENGINEER" ? String(a.residualLife.adoptedMonths) : "", error: "", notice: "", busy: false };
      if (!this.equipmentTag && this.assessmentCtx.description.tag) this.equipmentTag = this.assessmentCtx.description.tag;
    } catch (e) {
      console.warn("Could not load engineering assessment context", e);
      this.assessmentCtx = null;
    }
    try {
      this.riCorrections = await platform.technicalCorrectionService.listByDocument(this.reqCtx(), "INTEGRITY", this.reportId);
    } catch { this.riCorrections = []; }
    await this.loadMinutaTecnica();
    try { this.finalizeGate = await platform.engineeringService.finalizationGate(this.reqCtx(), this.reportId as any); } catch { this.finalizeGate = null; }
  }

  /** Fonte única da Minuta Técnica: hash da fonte → "ATUALIZAÇÃO NECESSÁRIA" só quando o hash difere. */
  private async loadMinutaTecnica() {
    if (!this.reportId) return;
    try {
      this.minutaTecnica = await platform.engineeringService.getMinutaTecnicaContext(this.reqCtx(), this.reportId as any);
      this.minutaNotes = this.minutaTecnica.minuta?.engineeringNotes || "";
    } catch (e) {
      console.warn("Could not load Minuta Técnica context", e);
      this.minutaTecnica = null;
    }
  }

  private async loadNdtRows() {
    if (!this.reportId || this.ndtLoaded) return;
    try {
      this.ndtRows = await platform.engineeringService.listNdtForReport(this.reqCtx(), this.reportId as any);
    } catch (e) { console.warn("Could not load END rows", e); this.ndtRows = []; }
    this.ndtLoaded = true;
  }

  private async refreshMinuta(message?: string) {
    await this.loadMinutaTecnica();
    await this.loadMinutaContext();
    try { this.finalizeGate = await platform.engineeringService.finalizationGate(this.reqCtx(), this.reportId as any); } catch { /* mantém */ }
    if (message) this.assessmentForm = { ...this.assessmentForm, notice: message, error: "" };
    this.render();
  }

  private async onUpdateMinuta() {
    if (!this.reportId) return;
    try {
      const ctx = await platform.engineeringService.updateMinutaFromReport(this.reqCtx(), this.reportId as any, "Atualização gerada pela Engenharia a partir do relatório.");
      await this.refreshMinuta(`Minuta atualizada para a Rev. ${ctx.minuta?.currentRevision}. Pendências: ${ctx.diff.pendingCount}.`);
    } catch (e: any) { alert(e?.safeMessage || e?.message || "Erro ao atualizar a Minuta."); }
  }

  private async onSaveMinutaDraft() {
    if (!this.reportId) return;
    try {
      await platform.engineeringService.saveMinutaDraft(this.reqCtx(), this.reportId as any, { engineeringNotes: this.minutaNotes });
      await this.refreshMinuta("Rascunho da Minuta salvo.");
    } catch (e: any) { alert(e?.safeMessage || e?.message || "Erro ao salvar o rascunho."); }
  }

  private async onValidateMinutaTecnica() {
    if (!this.reportId) return;
    if (!contextStore.hasPermission(PermissionCode.EngineeringValidateMinuta)) { alert("Você não possui permissão (engineering.validate_minuta) para validar a Minuta."); return; }
    if (!confirm("Confirma a validação da Minuta Técnica? Ela ficará como MINUTA VALIDADA, pronta para aprovação/assinatura do PLH.")) return;
    try {
      await platform.engineeringService.validateMinutaTecnica(this.reqCtx(), this.reportId as any);
      await this.refreshMinuta("Minuta Técnica validada.");
    } catch (e: any) { alert(e?.safeMessage || e?.message || "Erro ao validar a Minuta."); }
  }

  private openMinutaCorrectionModal() {
    const report = this.workspaceData?.report;
    if (!report) return;
    const recs = (this.workspaceData?.recommendations || []).map((r: any, i: number) => ({ id: String(r.id), label: `${r.recommendationNumber || r.code || `REC-${String(i + 1).padStart(3, "0")}`}${r.zr ? ` · ${r.zr}` : ""}${r.componentName || r.component ? ` — ${r.componentName || r.component}` : ""}` }));
    openCorrectionRequestModal({ documentType: "INTEGRITY", documentNumber: `Minuta ${this.minutaTecnica?.minuta?.trackingNumber || ""} · ${report.reportNumber || report.id}`, itemsBySection: { recommendations: recs }, confirmLabel: "SOLICITAR CORREÇÃO" }, async items => {
      const result = await platform.engineeringService.requestMinutaCorrections(this.reqCtx(), this.reportId as any, items);
      await this.loadAssessmentContext();
      await this.refreshMinuta(`Correção solicitada com ${result.corrections.length} item(ns); ${result.minutaRequests} recomendação(ões) entraram no ciclo da Minuta. O Inspetor foi notificado com navegação direta.`);
    });
  }

  private async onFinalizeReport() {
    if (!this.reportId) return;
    const gate = this.finalizeGate;
    if (!gate || !gate.allowed) { alert(`Não é possível finalizar: ${(gate?.reasons || ["gate indisponível"]).join("; ")}.`); return; }
    if (!confirm(`FINALIZAR RELATÓRIO?\n\nO snapshot final será gerado, a edição ordinária bloqueada e o status passará a ${gate.resultLabel}. Finalizar não é assinar.`)) return;
    try {
      const result = await platform.engineeringService.finalizeIntegrityReport(this.reqCtx(), this.reportId as any);
      if (this.workspaceData) this.workspaceData.report = result.report;
      await this.loadAssessmentContext();
      this.assessmentForm = { ...this.assessmentForm, notice: `Relatório finalizado: ${result.gate.resultLabel}.`, error: "" };
      this.render();
    } catch (e: any) { alert(e?.safeMessage || e?.message || "Erro ao finalizar o relatório."); }
  }

  /** FINALIZAR RELATÓRIO na barra principal — desabilitado com o motivo quando não pode. */
  private renderFinalizeButton(): string {
    const g = this.finalizeGate;
    const report: any = this.workspaceData?.report;
    if (report?.finalization) return `<span style="background: var(--status-success-bg); color: var(--status-success-text); border: 1px solid var(--status-success-border); padding: 8px 16px; border-radius: 6px; font-weight: 700;">${report.finalization.state === "FINALIZADO" ? "FINALIZADO" : "FINALIZADO — AGUARDANDO ASSINATURA"}</span>`;
    if (!contextStore.hasPermission(PermissionCode.IntegrityReportValidate)) return "";
    const allowed = !!g && g.allowed;
    const reason = g ? `Não é possível finalizar: ${g.reasons.join("; ")}.` : "Carregando pré-requisitos…";
    return `<button id="btn-finalize-report" ${allowed ? "" : "disabled"} title="${this.esc(allowed ? "Gerar snapshot final e bloquear a edição ordinária (não assina)." : reason)}" style="background: ${allowed ? "var(--status-success-solid)" : "var(--color-bg-secondary)"}; color: ${allowed ? "#fff" : "var(--color-graphite-500)"}; border: 1px solid ${allowed ? "var(--status-success-solid)" : "var(--color-graphite-300)"}; padding: 8px 16px; border-radius: 6px; font-weight: 700; cursor: ${allowed ? "pointer" : "not-allowed"};">FINALIZAR RELATÓRIO</button>${allowed ? "" : `<span style="font-size: 0.75rem; color: var(--status-danger-text); align-self: center; max-width: 420px;">${this.esc(reason)}</span>`}`;
  }

  private async onSaveAssessment() {
    if (!this.reportId || this.assessmentForm.busy) return;
    this.assessmentForm = { ...this.assessmentForm, busy: true, error: "", notice: "" };
    this.render();
    try {
      const avrFound = this.assessmentCtx?.avr.found === true;
      this.assessmentCtx = await platform.engineeringService.saveAssessment(this.reqCtx(), this.reportId as any, { equipmentCondition: this.assessmentForm.condition || null, residualLifeMonths: avrFound ? null : this.assessmentForm.months });
      if (this.workspaceData) this.workspaceData.report = this.assessmentCtx.report;
      const life = this.assessmentCtx.assessment.residualLife;
      this.assessmentForm = { ...this.assessmentForm, busy: false, notice: `Avaliação salva: ${EQUIPMENT_CONDITION_LABELS[this.assessmentForm.condition as EquipmentCondition]} → ${deriveEquipmentResult(this.assessmentForm.condition as EquipmentCondition)}${life ? ` · Vida Residual adotada: ${formatResidualLifeMonths(life.adoptedMonths)} (${RESIDUAL_LIFE_ORIGIN_LABELS[life.origin]})` : ""}.` };
    } catch (e: any) {
      this.assessmentForm = { ...this.assessmentForm, busy: false, error: e?.safeMessage || e?.message || "Erro ao salvar a avaliação." };
    }
    this.render();
  }

  private async onGenerateMinuta() {
    if (!this.reportId) return;
    try {
      const result = await platform.engineeringService.generateMinutaFromReport(this.reqCtx(), this.reportId as any);
      await this.loadAssessmentContext();
      try { this.finalizeGate = await platform.engineeringService.finalizationGate(this.reqCtx(), this.reportId as any); } catch { /* mantém */ }
      this.assessmentForm = { ...this.assessmentForm, notice: result.created ? `Minuta ${result.minuta.trackingNumber} gerada a partir dos dados do relatório. Abrindo a Minuta Técnica.` : `A inspeção já possui a Minuta ${result.minuta.trackingNumber}. Abrindo a Minuta Técnica.` };
      this.activeTab = "minuta";
      await this.loadMinutaContext();
    } catch (e: any) {
      this.assessmentForm = { ...this.assessmentForm, error: e?.safeMessage || e?.message || "Erro ao gerar a Minuta." };
      this.render();
    }
  }

  /** MANDAR PARA CORREÇÃO — itens exatos (seção / item / observação / prioridade) ao Inspetor responsável. */
  private openReturnModal() {
    const report = this.workspaceData?.report;
    if (!report) return;
    const recs = (this.workspaceData?.recommendations || []).map((r: any, i: number) => ({ id: String(r.id), label: `${r.recommendationNumber || r.code || `REC-${String(i + 1).padStart(3, "0")}`}${r.zr ? ` · ${r.zr}` : ""}${r.componentName || r.component ? ` — ${r.componentName || r.component}` : ""}` }));
    const comps = this.checklistComponents.map((c: any) => ({ id: String(c.id || c.name), label: String(c.name || c.definitionComponentId || c.id) }));
    openCorrectionRequestModal({ documentType: "INTEGRITY", documentNumber: report.reportNumber || String(report.id), itemsBySection: { recommendations: recs, checklist: comps } }, async items => {
      const result = await platform.engineeringService.returnForCorrectionWithItems(this.reqCtx(), this.reportId as any, items);
      if (this.workspaceData) this.workspaceData.report = result.report;
      await this.loadAssessmentContext();
      alert(`Relatório devolvido ao Inspetor com ${result.corrections.length} item(ns) de correção. O Inspetor foi notificado com navegação direta a cada item.`);
      router.navigate("/engineering");
    });
  }

  private renderMacroHeader(report: any): string {
    const d = this.assessmentCtx?.description;
    const workflow = workflowStatusOfIntegrityReport(report.status);
    const cell = (label: string, value: string) => `<div class="ws-macro-cell"><span>${label}</span><strong>${this.esc(value || "—")}</strong></div>`;
    const date = (iso: string) => (iso ? iso.split("T")[0]!.split("-").reverse().join("/") : "");
    return `
      <div class="ws-macro">
        ${cell("Tipo de documento", "RELATÓRIO DE INTEGRIDADE")}${cell("Nº do relatório", report.reportNumber || report.id)}${cell("Revisão", report.revision || "00")}${cell("Cliente", d?.client || "")}
        ${cell("Projeto", d?.project || "")}${cell("Unidade / Área", d?.unitArea || "")}${cell("TAG", d?.tag || this.equipmentTag)}${cell("Tipo de equipamento", d?.equipmentType || "")}
        ${cell("OS", d?.osNumber || "")}${cell("Sub-OS", d?.subOsNumber || "")}${cell("Inspetor responsável", d?.inspectorName || "")}${cell("Data da inspeção", date(d?.inspectionDate || ""))}
        ${cell("Data de envio", report.sentToPlhAt ? new Date(report.sentToPlhAt).toLocaleString("pt-BR") : "")}${cell("Responsável (Engenheiro do Projeto)", d?.engineerName || "")}${cell("PLH responsável", d?.plhName || "")}${cell("Status", workflow ? ENGINEERING_WORKFLOW_STATUS_LABELS[workflow] : this.mapStatus(report.status))}
      </div>`;
  }

  /** AVALIAÇÃO DE ENGENHARIA: condição (resultado derivado), Vida Residual (AVR ou Engenheiro, ≤ 240 meses), GERAR MINUTA. */
  private renderAssessmentBlock(): string {
    const ctx = this.assessmentCtx;
    const report = this.workspaceData?.report;
    if (!ctx || !report) return "";
    const f = this.assessmentForm;
    const a = ctx.assessment;
    const editable = (report.status === "SUBMITTED_TO_ENGINEERING" || report.status === "UNDER_ENGINEERING_REVIEW" || report.status === "APPROVED") && contextStore.hasPermission(PermissionCode.IntegrityReportEdit);
    const result = deriveEquipmentResult((f.condition || null) as EquipmentCondition | null);
    const life = a.residualLife;
    const avr = ctx.avr;
    const dateBR = (iso: string) => (iso ? iso.split("-").reverse().join("/") : "—");
    const options = (Object.keys(EQUIPMENT_CONDITION_LABELS) as EquipmentCondition[]).map(k => `<option value="${k}" ${f.condition === k ? "selected" : ""}>${EQUIPMENT_CONDITION_LABELS[k]}</option>`).join("");
    const canMinuta = contextStore.hasPermission(PermissionCode.EngineeringMinuteCreate);
    return `
      <div class="ws-assess" data-section="conclusion">
        <div class="ws-assess-head"><h3>Avaliação de Engenharia</h3><small>Classificação macro do equipamento e Vida Residual adotada — dados-fonte da Minuta.</small></div>
        <div class="ws-assess-grid">
          <label class="ws-field"><span>CONDIÇÃO DO EQUIPAMENTO *</span>
            <select id="assess-condition" ${editable ? "" : "disabled"}><option value="">— selecionar —</option>${options}</select></label>
          <label class="ws-field"><span>RESULTADO (derivado)</span><input readonly value="${this.esc(result || "—")}"></label>
          <label class="ws-field"><span>ORIGEM DA VIDA RESIDUAL</span><input readonly value="${this.esc(avr.found ? RESIDUAL_LIFE_ORIGIN_LABELS.AVR : (life ? RESIDUAL_LIFE_ORIGIN_LABELS[life.origin] : RESIDUAL_LIFE_ORIGIN_LABELS.ENGINEER))}"></label>
        </div>
        ${avr.found ? `
          <div class="ws-assess-avr">
            <div class="ws-assess-grid">
              <label class="ws-field"><span>VIDA RESIDUAL CALCULADA PELO AVR</span><input readonly value="${this.esc(`${formatResidualLifeMonths(avr.calculatedMonths)} (${avr.remainingLifeYears.toFixed(1).replace(".", ",")} anos)`)}"></label>
              <label class="ws-field"><span>FONTE</span><input readonly value="${this.esc(avr.avrNumber)}"></label>
              <label class="ws-field"><span>REVISÃO</span><input readonly value="${this.esc(avr.revision)}"></label>
              <label class="ws-field"><span>DATA</span><input readonly value="${this.esc(dateBR(avr.date))}"></label>
              <label class="ws-field"><span>VIDA RESIDUAL ADOTADA — MESES</span><input readonly class="${avr.capped ? "capped" : ""}" value="${this.esc(formatResidualLifeMonths(avr.adoptedMonths))}"></label>
            </div>
            <small>${avr.capped ? `O AVR calculou ${formatResidualLifeMonths(avr.calculatedMonths)}; a análise/Minuta adota o limite de ${RESIDUAL_LIFE_MAX_MONTHS} meses. O valor original do AVR é preservado e não é modificado.` : `Valor importado do AVR válido da TAG (${avr.avrNumber} / Rev. ${avr.revision}). O campo manual fica desabilitado.`}</small>
          </div>` : `
          <div class="ws-assess-grid">
            <label class="ws-field"><span>VIDA RESIDUAL — MESES *</span><input id="assess-months" type="number" min="1" max="${RESIDUAL_LIFE_MAX_MONTHS}" step="1" value="${this.esc(f.months)}" placeholder="1 a 240" ${editable ? "" : "disabled"}></label>
            <div class="ws-field"><span>&nbsp;</span><small>${ctx.relatedAvrs.length > 0 ? `Nenhum AVR aprovado disponível para Vida Residual. Existe AVR em elaboração/análise para a TAG ${this.esc(ctx.description.tag || this.equipmentTag || "")} (${ctx.relatedAvrs.map(a => `${this.esc(a.avrNumber)} — ${this.esc(a.statusLabel)}`).join(", ")}) — veja "Ver AVR".` : `Nenhum AVR encontrado para a TAG ${this.esc(ctx.description.tag || this.equipmentTag || "")}.`} Informe a Vida Residual (1 a 240 meses) — origem: INFORMADA PELO ENGENHEIRO.</small></div>
          </div>`}
        ${life ? `<div class="ws-assess-current">Avaliação gravada: <b>${this.esc(a.equipmentCondition ? EQUIPMENT_CONDITION_LABELS[a.equipmentCondition] : "—")}</b> → <b>${this.esc(a.equipmentResult || "—")}</b> · Vida Residual adotada <b>${this.esc(formatResidualLifeMonths(life.adoptedMonths))}</b>${life.calculatedMonths !== null && life.calculatedMonths !== life.adoptedMonths ? ` (AVR: ${this.esc(formatResidualLifeMonths(life.calculatedMonths))})` : ""} · ${this.esc(RESIDUAL_LIFE_ORIGIN_LABELS[life.origin])}${life.source ? ` · ${this.esc(life.source.avrNumber)} / Rev. ${this.esc(life.source.revision)}` : ""} · ${this.esc(a.updatedAt ? new Date(a.updatedAt).toLocaleString("pt-BR") : "")}</div>` : (a.equipmentCondition ? `<div class="ws-assess-current">Avaliação gravada: <b>${this.esc(EQUIPMENT_CONDITION_LABELS[a.equipmentCondition])}</b> → <b>${this.esc(a.equipmentResult || "—")}</b> · Vida Residual ainda não informada.</div>` : "")}
        ${f.error ? `<div class="ws-assess-msg err">${this.esc(f.error)}</div>` : ""}${f.notice ? `<div class="ws-assess-msg ok">${this.esc(f.notice)}</div>` : ""}
        <div class="ws-assess-actions">
          ${editable ? `<button id="btn-save-assessment" class="ws-btn primary" ${f.busy ? "disabled" : ""}>SALVAR AVALIAÇÃO</button>` : ""}
          ${canMinuta ? (ctx.minuta ? `<button id="btn-open-minuta" class="ws-btn">ABRIR MINUTA TÉCNICA (${this.esc(ctx.minuta.trackingNumber)} · ${this.esc(ctx.minuta.status)})</button>` : `<button id="btn-generate-minuta" class="ws-btn">GERAR MINUTA</button>`) : ""}
        </div>
      </div>`;
  }

  private renderRiCorrectionItems(): string {
    const rows = [...this.riCorrections].sort((a, b) => (a.status === "PENDENTE" ? 0 : 1) - (b.status === "PENDENTE" ? 0 : 1) || a.createdAt.localeCompare(b.createdAt));
    if (rows.length === 0) return "";
    return `
      <div class="ws-assess" style="margin-top:16px;">
        <div class="ws-assess-head"><h3>Correções por item (${rows.filter(c => c.status === "PENDENTE").length} pendente(s) de ${rows.length})</h3></div>
        <table class="ws-table"><thead><tr><th>#</th><th>Seção</th><th>Item</th><th>Observação</th><th>Prioridade</th><th>Inspetor</th><th>Status</th><th></th></tr></thead>
        <tbody>${rows.map(c => `<tr><td>${this.esc(c.sequence)}</td><td>${this.esc(c.sectionLabel)}</td><td>${this.esc(c.itemLabel || "—")}</td><td>${this.esc(c.comment)}</td><td>${this.esc(c.priority)}</td><td>${this.esc(c.assignedTo.name || "—")}</td><td>${this.esc(c.status)}</td><td><a href="${this.esc(c.anchor)}" data-link>Ir ao item</a></td></tr>`).join("")}</tbody></table>
      </div>`;
  }

  private async loadRiPdfSnapshot(reqContext: any) {
    if (!this.reportId || !platform.reportService?.buildDocumentSnapshot) {
      this.riPdfSnapshot = null;
      return;
    }
    try {
      this.riPdfSnapshot = await platform.reportService.buildDocumentSnapshot(reqContext, this.reportId as any);
    } catch (e) {
      console.warn("Could not build RI document snapshot", e);
      this.riPdfSnapshot = null;
    }
    this.render();
  }



  private async loadMinutaContext() {
    if (!this.workspaceData?.report?.inspectionId) return;
    this.isMinutaLoading = true;
    this.render();
    try {
      const ctx = contextStore.get();
      const reqContext = { activeClientId: ctx.activeClientId || "", permissions: ctx.permissions, correlationId: "", userId: ctx.userId } as any;
      const minuta = await platform.minutaService.getMinutaByInspection(reqContext, this.workspaceData.report.inspectionId as any);
      if (minuta) {
        this.minutaContext = await platform.engineeringService.getMinutaWorkspaceContext(reqContext, minuta.id);
        await this.resolveNamesForMinuta(reqContext);
        await this.loadMinutaPdfSnapshot(reqContext, minuta.id);
      } else {
        this.minutaContext = null;
        this.minutaPdfSnapshot = null;
      }
    } catch (e) {
      console.error("Erro ao carregar Minuta Context", e);
    } finally {
      this.isMinutaLoading = false;
      this.render();
    }
  }

  // Resolve userId -> display name for "Responsável" fields (Fase 12): the
  // Minuta only stores ids (analysisStartedBy/validatedByUserId/approvedBy
  // are all userId, never the name), so the UI must not print raw ids.
  private async resolveNamesForMinuta(reqContext: any) {
    if (!this.minutaContext) return;
    const { minuta, corrections } = this.minutaContext;
    const ids = [
      minuta.analysisStartedBy,
      minuta.validatedByUserId,
      minuta.approvedBy,
      ...corrections.map(c => c.requesterId),
      ...corrections.map(c => c.correctedBy),
      ...corrections.map(c => c.validatedBy)
    ].filter((id): id is string => Boolean(id));

    if (platform.adminService?.listUsers) {
      try {
        const allUsers = await platform.adminService.listUsers(reqContext);
        for (const u of allUsers) {
          if (u?.id && u?.name) {
            this.nameCache.set(u.id, u.name);
          }
        }
      } catch {}
    }
  }

  private displayName(id: string | null | undefined): string {
    if (!id) return '—';
    return this.nameCache.get(id) || id;
  }

  private async loadMinutaPdfSnapshot(reqContext: any, minutaId: string) {
    try {
      const revisions = await platform.minutaService.getRevisions(reqContext, minutaId);
      const currentRev = revisions.find((r: any) => r.status === 'ATUAL') || revisions[revisions.length - 1];
      if (currentRev?.snapshotData) {
        this.minutaPdfSnapshot = {
          id: currentRev.snapshotId,
          type: 'MINUTA',
          revision: currentRev.revisionNumber,
          createdAt: currentRev.createdAt,
          data: currentRev.snapshotData,
          documentNumber: this.minutaContext?.minuta.trackingNumber,
          // O documento identifica o próprio Cliente: gerar o PDF não pode
          // depender de haver um cliente global selecionado na sessão.
          clientId: this.workspaceData?.report?.clientId
        };
      } else {
        this.minutaPdfSnapshot = null;
      }
    } catch {
      this.minutaPdfSnapshot = null;
    }
  }

  private render() {
    if (!this.workspaceData) {
      return; // Handled by loader
    }

    const { report } = this.workspaceData;
    const canAnalyze = report.status === 'SUBMITTED_TO_ENGINEERING' || (report.status as any) === 'RESUBMITTED';
    const isAnalyzing = report.status === 'UNDER_ENGINEERING_REVIEW';
    const isApprovedOrSigned = report.status === 'AWAITING_SIGNATURE' || report.status === 'SIGNED' || report.status === 'COMPLETED' || (report.status as any) === 'ISSUED';
    
    const ctx = contextStore.get();
    const hasStartPerm = ctx?.permissions.has(PermissionCode.EngineeringStartAnalysis);

    const pendingCount = this.checklistComponents.filter(c => c.result === 'PENDING' || c.status === 'PENDING').length;

    this.innerHTML = `
      <div style="display: flex; flex-direction: column; height: 100vh; background: var(--color-bg-primary);">
        <!-- Workspace Header -->
        <header style="background: var(--color-surface-primary); border-bottom: 1px solid var(--color-graphite-200); padding: 16px 24px; display: flex; justify-content: space-between; align-items: center;">
          <div style="display: flex; align-items: center; gap: 16px;">
            <button id="btn-back" style="background: none; border: none; font-size: 1.25rem; cursor: pointer; color: var(--color-text-secondary);">←</button>
            <div>
              <h1 style="margin: 0; font-size: 1.25rem; color: var(--color-text-primary);">Análise Técnica do Relatório de Integridade</h1>
              <p style="margin: 4px 0 0 0; font-size: 0.875rem; color: var(--color-text-secondary);">${this.esc(report.reportNumber || report.id)} • Rev. ${this.esc(report.revision || "00")} • Status: ${this.mapStatus(report.status)}</p>
            </div>
          </div>
          <div style="display: flex; gap: 12px; flex-wrap: wrap; justify-content: flex-end;">
            ${this.renderFinalizeButton()}
            ${canAnalyze ? 
              (hasStartPerm ? `<button id="btn-start" style="background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8); border: none; padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer; box-shadow: 0 2px 8px rgba(0,0,0,0.2);">INICIAR ANÁLISE</button>` 
              : `<div style="font-size: 0.8125rem; color: var(--status-danger-text); font-weight: 500;">Você não possui permissão para iniciar esta análise.</div>`)
             : ''}
            ${isAnalyzing ? `
              <button id="btn-return" style="background: var(--color-surface-primary); color: var(--status-danger-solid); border: 1px solid var(--status-danger-solid); padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer;">MANDAR PARA CORREÇÃO</button>
              <button id="btn-approve" style="background: var(--status-success-text); color: var(--color-text-primary); border: none; padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer;">Aprovar Técnica (PLH)</button>
            ` : ''}
            ${isApprovedOrSigned ? `
              <button id="btn-review-release" style="background: var(--color-primary); color: var(--color-text-primary); border: none; padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer;">Revisar Liberação</button>
            ` : ''}
          </div>
        </header>

        ${this.renderMacroHeader(report)}
        <div style="display: flex; flex: 1; overflow: hidden;">
          <!-- Sidebar Tabs -->
          <nav style="width: 200px; background: var(--color-surface-primary); border-right: 1px solid var(--color-graphite-200); padding: 16px 0; display: flex; flex-direction: column;">
            ${this.renderTabItem("summary", "Visão Macro & Avaliação")}
            ${this.renderTabItem("ri", "Ver Relatório / PDF")}
            ${this.renderTabItem("checklist", "Ver Checklist & Fotos")}
            ${this.renderTabItem("minuta", "Minuta Técnica")}
            ${this.renderTabItem("ndt", "END (Ensaios)")}
            ${this.renderTabItem("thickness", "Ver AVR")}
            ${this.renderTabItem("ai-review", "INSPECT IA REVIEW")}
          </nav>

          <!-- Main Content -->
          <main style="flex: 1; overflow-y: auto; padding: 24px; position: relative;">
            <!-- Bloqueio visual removido, apenas ações são bloqueadas via TS -->
            
            ${pendingCount > 0 && this.activeTab !== 'minuta' ? `
              <div style="background: var(--status-danger-bg); border: 1px solid var(--status-danger-border); border-radius: 8px; padding: 16px; margin-bottom: 20px; display: flex; align-items: flex-start; gap: 12px; box-shadow: 0 1px 2px rgba(0,0,0,0.05);">
                <span style="font-size: 1.5rem; line-height: 1;">⚠️</span>
                <div style="flex: 1;">
                  <h3 style="color: var(--status-danger-text); font-size: 0.9375rem; font-weight: 700; margin: 0;">Pendências de Preenchimento no Checklist Técnico</h3>
                  <p style="color: var(--status-danger-text); font-size: 0.8125rem; margin: 4px 0 0 0;">Este laudo possui <strong>${pendingCount} componente(s) pendente(s) de inspeção</strong> no Checklist Técnico de campo. Todas as pendências devem ser resolvidas antes da aprovação final pelo Engenheiro PLH.</p>
                </div>
              </div>
            ` : ''}

            ${this.activeTab === 'summary' ? this.renderSummaryTab() : ''}
            ${this.activeTab === 'checklist' ? this.renderChecklistTab() : ''}
            ${this.activeTab === 'ndt' ? this.renderNdtTab() : ''}
            ${this.activeTab === 'thickness' ? this.renderThicknessTab() : ''}
            ${this.activeTab === 'minuta' ? this.renderMinutaTab() : ''}
            ${this.activeTab === 'ri' ? this.renderRiTab() : ''}
            ${this.activeTab === 'ai-review' ? this.renderAiReviewTab() : ''}
          </main>
        </div>
      </div>
      ${this.renderModals()}
      <style>
        ${PLH_REVIEW_UI_STYLES}
        ${CORRECTION_UI_STYLES}
        .ws-macro { display:grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap:8px 14px; padding:10px 24px; background: var(--color-surface-secondary); border-bottom:1px solid var(--color-graphite-200); }
        .ws-macro-cell { display:flex; flex-direction:column; gap:2px; min-width:0; } .ws-macro-cell span { font-size:0.62rem; text-transform:uppercase; letter-spacing:0.05em; color: var(--color-text-secondary); } .ws-macro-cell strong { font-size:0.82rem; color: var(--color-text-primary); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .ws-assess { background: var(--color-surface-primary); border:1px solid var(--color-graphite-200); border-radius:8px; padding:16px 20px; display:flex; flex-direction:column; gap:12px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
        .ws-assess-head h3 { margin:0; font-size:1rem; font-weight:700; color: var(--color-text-muted); } .ws-assess-head small { color: var(--color-text-secondary); font-size:0.78rem; }
        .ws-assess-grid { display:grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap:10px; }
        .ws-field { display:flex; flex-direction:column; gap:4px; font-size:0.68rem; color: var(--color-text-secondary); } .ws-field span { text-transform:uppercase; letter-spacing:0.04em; font-weight:700; }
        .ws-field input, .ws-field select { padding:8px 10px; border:1px solid var(--color-graphite-300); border-radius:6px; font-size:0.85rem; background: var(--color-surface-primary); color: var(--color-text-primary); } .ws-field input[readonly] { background: var(--color-bg-secondary); font-weight:600; } .ws-field input.capped { border-color:#d97706; }
        .ws-assess-avr { border:1px dashed var(--color-graphite-300); border-radius:8px; padding:12px; display:flex; flex-direction:column; gap:8px; } .ws-assess-avr small, .ws-field small { color: var(--color-text-secondary); font-size:0.75rem; }
        .ws-assess-current { font-size:0.82rem; color: var(--color-text-secondary); padding:8px 10px; background: var(--color-bg-secondary); border-radius:6px; }
        .ws-assess-msg { padding:8px 10px; border-radius:6px; font-size:0.82rem; } .ws-assess-msg.err { background: var(--status-danger-bg); color: var(--status-danger-text); border:1px solid var(--status-danger-border); } .ws-assess-msg.ok { background: var(--status-success-bg); color: var(--status-success-text); border:1px solid var(--status-success-border); }
        .ws-assess-actions { display:flex; gap:8px; flex-wrap:wrap; }
        .ws-btn { background: var(--color-surface-secondary); border:1px solid var(--color-graphite-300); border-radius:6px; padding:8px 14px; font-size:0.8rem; font-weight:700; cursor:pointer; color: var(--color-text-primary); } .ws-btn.primary { background: var(--ia-action-bg, var(--color-primary)); border-color: var(--color-primary); color: var(--ia-action-fg, #F7F8F8); } .ws-btn[disabled] { opacity:0.5; cursor:not-allowed; }
        .ws-status { display:inline-block; padding:2px 8px; border-radius:12px; font-size:0.7rem; font-weight:700; text-transform:uppercase; background: var(--color-surface-secondary); color: var(--color-text-secondary); white-space:nowrap; }
        .ws-table { width:100%; border-collapse:collapse; font-size:0.8rem; } .ws-table th { text-align:left; font-size:0.66rem; text-transform:uppercase; color: var(--color-text-secondary); padding:6px 8px; border-bottom:1px solid var(--color-graphite-200); } .ws-table td { padding:6px 8px; border-bottom:1px solid var(--color-bg-secondary); vertical-align:top; }
      </style>
    `;

    this.attachEvents();
  }

  private renderTabItem(id: string, label: string) {
    const isActive = this.activeTab === id;
    return `
      <button class="workspace-tab ${isActive ? 'active' : ''}" data-tab="${id}" style="text-align: left; background: ${isActive ? 'var(--color-bg-secondary)' : 'transparent'}; border: none; border-right: 3px solid ${isActive ? 'var(--color-ink)' : 'transparent'}; padding: 12px 24px; cursor: pointer; color: ${isActive ? 'var(--color-graphite-800)' : 'var(--color-graphite-500)'}; font-weight: ${isActive ? '600' : '500'}; font-size: 0.875rem;">
        ${label}
      </button>
    `;
  }

  private renderSummaryTab() {
    const report = this.workspaceData?.report;
    const comps = this.checklistComponents;
    const totalComps = comps.length;
    const pendingComps = comps.filter(c => c.result === 'PENDING' || c.status === 'PENDING').length;
    const approvedComps = comps.filter(c => c.result === 'APPROVED' || c.status === 'APPROVED').length;
    const rejectedComps = comps.filter(c => c.result === 'REJECTED' || c.status === 'REJECTED').length;

    const analysisStatus = report ? this.mapStatus(report.status) : 'N/A';
    // RESPONSÁVEL = Engenheiro responsável vinculado ao Projeto (fonte de verdade) — nunca o criador, o último usuário, o inspetor ou o PLH.
    const responsibleStr = this.assessmentCtx?.description.engineerName ? this.assessmentCtx.description.engineerName + " (Engenheiro do Projeto)" : "Projeto sem Engenheiro responsável — defina em Gestão → Projetos";
    let startedAtStr = "—";
    if (this.minutaContext?.minuta.analysisStartedAt) {
      startedAtStr = new Date(this.minutaContext.minuta.analysisStartedAt).toLocaleString('pt-BR');
    }

    return `
      <div style="display: flex; flex-direction: column; gap: 24px;">
        <div>
          <h2 style="color: var(--color-text-muted); font-size: 1.25rem; font-weight: 700; margin: 0;">Resumo Técnico da Inspeção</h2>
          <p style="color: var(--color-text-secondary); font-size: 0.875rem; margin: 4px 0 0 0;">Dados gerais do Relatório de Integridade e progresso de campo.</p>
        </div>

        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 16px;">
          <div style="background: var(--color-surface-secondary); padding: 16px; border-radius: 8px; border: 1px solid var(--color-graphite-200);">
            <div style="font-size: 0.75rem; font-weight: 700; color: var(--color-text-muted); text-transform: uppercase; margin-bottom: 8px;">STATUS DA ANÁLISE</div>
            <div style="font-size: 1.5rem; font-weight: 700; color: var(--ia-primary);">${analysisStatus}</div>
            <div style="font-size: 0.875rem; color: var(--color-text-secondary); margin-top: 4px;">Responsável: ${responsibleStr}</div>
            <div style="font-size: 0.875rem; color: var(--color-text-secondary); margin-top: 2px;">Início: ${startedAtStr}</div>
          </div>
          <div style="background: var(--color-surface-primary); border: 1px solid var(--color-graphite-200); border-radius: 8px; padding: 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.05);">
            <span style="font-size: 0.75rem; font-weight: 600; color: var(--color-text-secondary); text-transform: uppercase;">Número do Relatório</span>
            <p style="font-size: 1.125rem; font-weight: 700; color: var(--color-ink); margin: 6px 0 0 0;">${report?.reportNumber || report?.id || 'N/A'}</p>
          </div>
          <div style="background: var(--color-surface-primary); border: 1px solid var(--color-graphite-200); border-radius: 8px; padding: 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.05);">
            <span style="font-size: 0.75rem; font-weight: 600; color: var(--color-text-secondary); text-transform: uppercase;">TAG do Equipamento</span>
            <p style="font-size: 1.125rem; font-weight: 700; color: var(--color-text-muted); margin: 6px 0 0 0;">${this.equipmentTag || 'N/A'}</p>
          </div>
          <div style="background: var(--color-surface-primary); border: 1px solid var(--color-graphite-200); border-radius: 8px; padding: 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.05);">
            <span style="font-size: 0.75rem; font-weight: 600; color: var(--color-text-secondary); text-transform: uppercase;">Status de Processamento</span>
            <p style="font-size: 1.125rem; font-weight: 700; color: var(--color-text-muted); margin: 6px 0 0 0;">${report?.status || 'N/A'}</p>
          </div>
        </div>

        ${this.renderAssessmentBlock()}
        ${this.renderRiCorrectionItems()}

        <div style="background: var(--color-surface-primary); border: 1px solid var(--color-graphite-200); border-radius: 8px; padding: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.05);">
          <h3 style="color: var(--color-text-muted); font-size: 1rem; font-weight: 700; margin: 0 0 16px 0;">Status do Checklist Técnico</h3>
          
          <div style="display: flex; align-items: center; gap: 24px; flex-wrap: wrap;">
            <!-- Progress Circle or Percentage -->
            <div style="display: flex; flex-direction: column; gap: 4px;">
              <span style="font-size: 2rem; font-weight: 800; color: ${pendingComps > 0 ? 'var(--status-warning-solid)' : 'var(--status-success-solid)'}; line-height: 1;">
                ${totalComps > 0 ? Math.round(((totalComps - pendingComps) / totalComps) * 100) : 0}%
              </span>
              <span style="font-size: 0.75rem; font-weight: 600; color: var(--color-text-secondary); text-transform: uppercase; letter-spacing: 0.05em;">Conclusão de Campo</span>
            </div>

            <!-- Detail stats -->
            <div style="display: flex; gap: 16px; flex: 1; min-width: 250px;">
              <div style="flex: 1; background: var(--color-bg-primary); border: 1px solid var(--color-graphite-200); border-radius: 6px; padding: 10px; text-align: center;">
                <span style="font-size: 1.25rem; font-weight: 700; color: var(--color-ink);">${totalComps}</span>
                <p style="margin: 2px 0 0 0; font-size: 0.75rem; color: var(--color-text-secondary); font-weight: 500;">Total Itens</p>
              </div>
              <div style="flex: 1; background: var(--status-success-bg); border: 1px solid var(--status-success-border); border-radius: 6px; padding: 10px; text-align: center;">
                <span style="font-size: 1.25rem; font-weight: 700; color: var(--status-success-text);">${approvedComps}</span>
                <p style="margin: 2px 0 0 0; font-size: 0.75rem; color: var(--status-success-text); font-weight: 500;">Conformes</p>
              </div>
              <div style="flex: 1; background: var(--status-warning-bg); border: 1px solid var(--status-warning-border); border-radius: 6px; padding: 10px; text-align: center;">
                <span style="font-size: 1.25rem; font-weight: 700; color: var(--status-warning-text);">${rejectedComps}</span>
                <p style="margin: 2px 0 0 0; font-size: 0.75rem; color: var(--status-warning-text); font-weight: 500;">Não Conformes</p>
              </div>
              <div style="flex: 1; background: var(--status-danger-bg); border: 1px solid var(--status-danger-border); border-radius: 6px; padding: 10px; text-align: center;">
                <span style="font-size: 1.25rem; font-weight: 700; color: var(--status-danger-solid);">${pendingComps}</span>
                <p style="margin: 2px 0 0 0; font-size: 0.75rem; color: var(--status-danger-solid); font-weight: 600;">Pendentes</p>
              </div>
            </div>
          </div>

          ${pendingComps > 0 ? `
            <div style="margin-top: 16px; background: var(--status-danger-bg); border: 1px solid var(--status-danger-bg); border-radius: 6px; padding: 12px; display: flex; align-items: center; gap: 10px;">
              <span style="color: var(--status-danger-solid); font-weight: bold; font-size: 1.1rem;">⚠️</span>
              <p style="margin: 0; font-size: 0.8125rem; color: var(--status-danger-text); font-weight: 500;">Atenção Engenheiro: Este checklist possui <strong>${pendingComps} pendências de preenchimento</strong> no campo e não está elegível para liberação técnica.</p>
            </div>
          ` : `
            <div style="margin-top: 16px; background: var(--status-success-bg); border: 1px solid var(--status-success-border); border-radius: 6px; padding: 12px; display: flex; align-items: center; gap: 10px;">
              <span style="color: var(--status-success-text); font-weight: bold; font-size: 1.1rem;">✅</span>
              <p style="margin: 0; font-size: 0.8125rem; color: var(--status-success-text); font-weight: 500;">Todos os componentes foram devidamente inspecionados pelo inspetor de campo.</p>
            </div>
          `}
        </div>
      </div>
    `;
  }

  // Bloco 9, Parte C: matches a Checklist component to its Recommendation
  // (never invents one — mirrors the same matching already used server-side
  // in ReportService.buildDocumentSnapshot).
  private findRecommendationForComponent(c: any): any {
    const recs = this.workspaceData?.recommendations || [];
    const cName = (c.definitionComponentId || c.groupId || c.name || '').toLowerCase().trim();
    return recs.find((r: any) => {
      if (r.checklistItemId && r.checklistItemId === c.id) return true;
      if (r.componentId && r.componentId === c.id) return true;
      if (r.componentName && cName && r.componentName.toLowerCase().trim() === cName) return true;
      return false;
    }) || null;
  }

  // Separates "RESULTADO DA INSPEÇÃO" (field checklist result) from
  // "STATUS DA ENGENHARIA" (Fase 40/41) — an item can have an anomaly and
  // still be VALIDADO by Engineering; the two are never conflated.
  private getEngineeringStatusForRecommendation(recommendationId: string | undefined): { label: string; bg: string; text: string; canValidate: boolean; correctionId?: string } {
    if (!recommendationId || !this.minutaContext) {
      return { label: 'Sem Recommendation', bg: 'var(--color-bg-secondary)', text: 'var(--color-text-secondary)', canValidate: false };
    }
    const correction = this.minutaContext.corrections.find(c => c.recommendationId === recommendationId);
    if (!correction) {
      return { label: 'Pendente de Revisão', bg: '#5C5C5C', text: '#EEEEEE', canValidate: true };
    }
    switch (correction.status) {
      case 'PENDENTE':
      case 'EM CORREÇÃO':
      case 'DEVOLVIDA':
        return { label: 'Aguardando Correção', bg: '#7f1d1d', text: '#FFFFFF', canValidate: false, correctionId: correction.id };
      case 'AGUARDANDO VALIDAÇÃO':
      case 'CORRIGIDA':
        return { label: 'Aguardando Validação', bg: '#92400e', text: '#FFFFFF', canValidate: true, correctionId: correction.id };
      case 'VALIDADA':
        return { label: 'Validado', bg: '#166534', text: '#FFFFFF', canValidate: false, correctionId: correction.id };
      default:
        return { label: 'Pendente de Revisão', bg: '#5C5C5C', text: '#EEEEEE', canValidate: true, correctionId: correction.id };
    }
  }

  private renderChecklistTab() {
    const comps = this.checklistComponents;
    if (comps.length === 0) {
      return `
        <div style="background: var(--color-surface-primary); border-radius: 8px; border: 1px solid var(--color-graphite-200); padding: 24px; text-align: center; color: var(--color-text-secondary);">
          <p style="margin: 0;">Nenhum componente do checklist técnico registrado para este equipamento.</p>
        </div>
      `;
    }

    const pendingCount = comps.filter(c => c.result === 'PENDING' || c.status === 'PENDING').length;

    return `
      <div style="display: flex; flex-direction: column; gap: 20px;">
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <div>
            <h2 style="color: var(--color-text-muted); font-size: 1.25rem; font-weight: 700; margin: 0;">Checklist de Inspeção Visual</h2>
            <p style="color: var(--color-text-secondary); font-size: 0.875rem; margin: 4px 0 0 0;">Análise detalhada das constatações de campo e evidências fotográficas.</p>
          </div>
          <div style="display: flex; flex-direction: column; gap: 6px; align-items: flex-end;">
            ${pendingCount > 0 ? `
              <span style="background: var(--status-danger-bg); color: var(--status-danger-solid); border: 1px solid var(--status-danger-bg); padding: 6px 12px; border-radius: 6px; font-size: 0.875rem; font-weight: 700;">
                ⚠️ CHECKLIST DE CAMPO: ${pendingCount} PENDÊNCIA(S)
              </span>
            ` : `
              <span style="background: var(--status-success-bg); color: var(--status-success-text); border: 1px solid var(--status-success-border); padding: 6px 12px; border-radius: 6px; font-size: 0.875rem; font-weight: 700;">
                ✅ CHECKLIST DE CAMPO: COMPLETO
              </span>
            `}
            ${this.minutaContext ? (() => {
              const engPending = comps.filter(c => {
                const r = this.findRecommendationForComponent(c);
                return r && this.getEngineeringStatusForRecommendation(r.id).canValidate;
              }).length;
              return engPending > 0
                ? `<span style="background: #5C5C5C; color: var(--ia-on-color, #EEEEEE); border: 1px solid #909090; padding: 6px 12px; border-radius: 6px; font-size: 0.875rem; font-weight: 700;">ANÁLISE DE ENGENHARIA: ${engPending} ITEM(NS) PENDENTE(S)</span>`
                : `<span style="background: var(--ia-success-bg, #166534); color: #FFFFFF; border: 1px solid #22c55e; padding: 6px 12px; border-radius: 6px; font-size: 0.875rem; font-weight: 700;">ANÁLISE DE ENGENHARIA: EM DIA</span>`;
            })() : ''}
          </div>
        </div>

        <div style="display: flex; flex-direction: column; gap: 16px;">
          ${comps.map(c => {
            const status = c.result || c.status || 'PENDING';
            let statusLabel = 'Pendente';
            let statusBg = 'var(--status-danger-bg)';
            let statusColor = 'var(--status-danger-solid)';
            let statusBorder = 'var(--status-danger-bg)';

            if (status === 'APPROVED') {
              statusLabel = 'Conforme (Aprovado)';
              statusBg = 'var(--status-success-bg)';
              statusColor = 'var(--status-success-text)';
              statusBorder = 'var(--status-success-border)';
            } else if (status === 'REJECTED') {
              statusLabel = 'Não Conforme (Reprovado)';
              statusBg = 'var(--status-warning-bg)';
              statusColor = 'var(--status-warning-text)';
              statusBorder = 'var(--status-warning-border)';
            } else if (status === 'NOT_APPLICABLE') {
              statusLabel = 'Não Aplicável';
              statusBg = 'var(--color-bg-secondary)';
              statusColor = 'var(--color-graphite-600)';
              statusBorder = 'var(--color-graphite-300)';
            } else if (status === 'CLIENT_DISCRETION') {
              statusLabel = 'A Critério do Cliente';
              statusBg = 'var(--color-surface-secondary)';
              statusColor = 'var(--color-primary)';
              statusBorder = 'var(--color-border-subtle)';
            }

            const rec = this.findRecommendationForComponent(c);
            const engStatus = this.getEngineeringStatusForRecommendation(rec?.id);
            const isAnalyzing = this.minutaContext?.minuta.status === 'EM ANÁLISE';
            const ctx = contextStore.get();
            const permsChk = ctx?.permissions;
            const hasChk = (p: string) => permsChk?.has(p) ?? false;

            return `
              <div style="background: var(--color-surface-primary); border: 1px solid ${status === 'PENDING' ? 'var(--status-danger-bg)' : 'var(--color-graphite-200)'}; border-radius: 8px; padding: 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.05);">
                <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; flex-wrap: wrap;">
                  <div>
                    <span style="font-size: 0.75rem; font-family: monospace; color: var(--color-text-secondary); font-weight: 600;">COMPONENTE ${c.index || ''}</span>
                    <h3 style="color: var(--color-text-muted); font-size: 1.125rem; font-weight: 700; margin: 4px 0 8px 0;">${c.definitionComponentId || c.groupId || 'Componente'}</h3>
                  </div>
                  <div style="display: flex; flex-direction: column; gap: 6px; align-items: flex-end;">
                    <span style="background: ${statusBg}; color: ${statusColor}; border: 1px solid ${statusBorder}; padding: 4px 10px; border-radius: 12px; font-size: 0.75rem; font-weight: 700; text-transform: uppercase;">
                      RESULTADO DA INSPEÇÃO: ${statusLabel}
                    </span>
                    <span style="background: ${engStatus.bg}; color: ${engStatus.text}; padding: 4px 10px; border-radius: 12px; font-size: 0.75rem; font-weight: 700; text-transform: uppercase;">
                      ENGENHARIA: ${engStatus.label}
                    </span>
                  </div>
                </div>

                ${c.description ? `
                  <div style="margin-top: 12px; background: var(--color-bg-primary); padding: 10px 14px; border-radius: 6px; border-left: 3px solid var(--color-graphite-300);">
                    <p style="color: var(--color-text-muted); font-size: 0.875rem; margin: 0; font-weight: 500;">Constatação:</p>
                    <p style="color: var(--color-text-secondary); font-size: 0.875rem; margin: 4px 0 0 0;">${c.description}</p>
                  </div>
                ` : ''}

                ${c.inspectionNotes ? `
                  <div style="margin-top: 8px; background: var(--status-warning-bg); padding: 10px 14px; border-radius: 6px; border-left: 3px solid var(--status-warning-solid);">
                    <p style="color: var(--status-warning-text); font-size: 0.875rem; margin: 0; font-weight: 500;">Comentário Técnico / Anotações:</p>
                    <p style="color: var(--ia-fg, #EEEEEE); font-size: 0.875rem; margin: 4px 0 0 0;">${c.inspectionNotes}</p>
                  </div>
                ` : ''}

                ${this.minutaContext ? `
                  <div style="margin-top: 12px; display: flex; gap: 8px; flex-wrap: wrap;">
                    ${rec ? `
                      ${isAnalyzing && hasChk(PermissionCode.EngineeringEditRecommendation) ? `<button class="btn-edit-rec" data-id="${rec.id}" style="background: none; border: 1px solid var(--color-primary); color: var(--ia-primary); padding: 4px 12px; border-radius: 4px; cursor: pointer; font-size: 0.75rem;">Editar Recomendação</button>` : ''}
                      ${isAnalyzing && hasChk(PermissionCode.EngineeringRequestCorrection) && hasChk(PermissionCode.EngineeringDelegateToInspector) ? `<button class="btn-req-corr" data-id="${rec.id}" style="background: none; border: 1px solid var(--status-danger-solid); color: var(--status-danger-solid); padding: 4px 12px; border-radius: 4px; cursor: pointer; font-size: 0.75rem;">Solicitar Correção / Devolver</button>` : ''}
                      ${isAnalyzing && engStatus.canValidate && hasChk(PermissionCode.EngineeringValidateCorrection) ? `<button class="btn-validate-item" data-recid="${rec.id}" style="background: var(--ia-success-bg, #166534); border: 1px solid #22c55e; color: #FFFFFF; padding: 4px 12px; border-radius: 4px; cursor: pointer; font-size: 0.75rem; font-weight: 600;">Validar Item</button>` : ''}
                      ${engStatus.correctionId ? `<button class="btn-item-history" data-corrid="${engStatus.correctionId}" style="background: none; border: 1px solid var(--color-graphite-300); color: var(--color-text-secondary); padding: 4px 12px; border-radius: 4px; cursor: pointer; font-size: 0.75rem;">Histórico</button>` : ''}
                    ` : `
                      <span style="font-size: 0.75rem; color: var(--color-text-secondary); font-style: italic;">Nenhuma Recommendation vinculada a este componente.</span>
                    `}
                  </div>
                ` : ''}

                <!-- Photos -->
                ${c.photos && c.photos.length > 0 ? `
                  <div style="margin-top: 16px;">
                    <h4 style="color: var(--color-text-secondary); font-size: 0.8125rem; font-weight: 600; margin: 0 0 8px 0; text-transform: uppercase; letter-spacing: 0.05em;">Fotos / Evidências (${c.photos.length})</h4>
                    <div style="display: flex; gap: 12px; flex-wrap: wrap;">
                      ${c.photos.map((ph: any) => `
                        <div style="width: 200px; border: 1px solid var(--color-graphite-200); border-radius: 6px; overflow: hidden; background: var(--color-bg-primary);">
                          <img src="${ph.url}" style="width: 100%; height: 130px; object-fit: cover; display: block;" />
                          ${ph.comment ? `
                            <div style="padding: 8px; border-top: 1px solid var(--color-graphite-200); background: var(--color-surface-primary);">
                              <p style="margin: 0; font-size: 0.75rem; color: var(--color-text-secondary); line-height: 1.3;">${ph.comment}</p>
                            </div>
                          ` : ''}
                        </div>
                      `).join('')}
                    </div>
                  </div>
                ` : ''}
              </div>
            `;
          }).join('')}
        </div>
      </div>
    `;
  }

  /** END (Ensaios): relatórios END do contexto (Cliente/Projeto/Unidade/TAG) com o estado na Engenharia e ANALISAR. */
  private renderNdtTab() {
    const rows = this.ndtRows;
    const cell = (v: string) => `<td>${this.esc(v || "—")}</td>`;
    const date = (iso: string | null) => (iso ? new Date(iso).toLocaleString("pt-BR") : "—");
    const dateBR = (d: string) => (d ? d.split("T")[0]!.split("-").reverse().join("/") : "—");
    return `<div style="display:flex; flex-direction:column; gap:12px;">
      <div><h2 style="margin:0; color: var(--color-text-muted); font-size:1.25rem;">Ensaios Não Destrutivos (END)</h2><p style="margin:4px 0 0 0; color: var(--color-text-secondary); font-size:0.875rem;">LP, ME, PM, US-Convencional (e B-Scan quando houver módulo) do mesmo Cliente/Projeto/Unidade/TAG, com o estado na Engenharia. ANALISAR abre a análise técnica do END.</p></div>
      ${!this.ndtLoaded ? `<div class="ws-assess">Carregando ensaios…</div>` : `
      <div class="ws-assess" style="padding:0; overflow-x:auto;">
        <table class="ws-table">
          <thead><tr>${["Tipo", "Nº Relatório", "Revisão", "TAG", "Data do ensaio", "Inspetor", "Status", "Data de envio", "Ação"].map(h => `<th>${h.toUpperCase()}</th>`).join("")}</tr></thead>
          <tbody>${rows.length === 0 ? `<tr><td colspan="9" style="padding:22px; text-align:center; color: var(--color-text-secondary);">Nenhum relatório END encontrado para este contexto (Cliente/Projeto/Unidade/TAG).</td></tr>` : rows.map(r => `<tr data-item="${this.esc(r.documentNumber)}">
            <td><b>${this.esc(r.typeLabel)}</b></td>${cell(r.documentNumber)}${cell(r.revision)}${cell(r.tag)}${cell(dateBR(r.testDate))}${cell(r.inspectorName)}<td><span class="ws-status">${this.esc(r.statusLabel)}</span></td>${cell(date(r.submittedAt))}
            <td style="white-space:nowrap;">${r.reviewRoute ? `<button class="ws-btn primary btn-ndt-route" data-route="${this.esc(r.reviewRoute)}">ANALISAR</button> ` : ""}<button class="ws-btn btn-ndt-route" data-route="${this.esc(r.documentRoute)}">Ver relatório</button></td>
          </tr>`).join("")}</tbody>
        </table>
      </div>`}
    </div>`;
  }

  private renderThicknessTab() {
    const avr = this.assessmentCtx?.avr;
    const tag = this.assessmentCtx?.description.tag || this.equipmentTag;
    return `<div style="display:flex; flex-direction:column; gap:12px;">
      <h2 style="margin:0; color: var(--color-text-muted); font-size:1.25rem;">Espessuras e AVR</h2>
      <p style="margin:0; color: var(--color-text-secondary); font-size:0.875rem;">Vida Residual do equipamento a partir do AVR válido da TAG — resultado do motor API 570 (fonte: relatórios ME/B-Scan). O RI não recalcula nada.</p>
      ${this.renderRelatedAvrs()}
      ${avr && avr.found
        ? `<div class="ws-assess"><div class="ws-assess-grid">
            <label class="ws-field"><span>AVR</span><input readonly value="${this.esc(avr.avrNumber)} / Rev. ${this.esc(avr.revision)}"></label>
            <label class="ws-field"><span>Data</span><input readonly value="${this.esc(avr.date.split("-").reverse().join("/"))}"></label>
            <label class="ws-field"><span>Vida remanescente (ponto crítico)</span><input readonly value="${this.esc(avr.remainingLifeYears.toFixed(2).replace(".", ","))} anos = ${this.esc(formatResidualLifeMonths(avr.calculatedMonths))}"></label>
            <label class="ws-field"><span>Vida Residual adotada</span><input readonly value="${this.esc(formatResidualLifeMonths(avr.adoptedMonths))}${avr.capped ? " (limite 240)" : ""}"></label>
          </div><div class="ws-assess-actions"><button class="ws-btn" id="btn-open-avr" data-avr="${this.esc(avr.avrId)}">VER AVR</button></div></div>`
        : `<div class="ws-assess"><p style="margin:0; font-size:0.875rem;">${(this.assessmentCtx?.relatedAvrs.length || 0) > 0 ? `Nenhum AVR aprovado disponível para Vida Residual. Existe AVR em elaboração/análise para a TAG ${this.esc(tag || "—")} (acima).` : `Nenhum AVR encontrado para a TAG ${this.esc(tag || "—")}.`} A Vida Residual é informada pelo Engenheiro na aba "Visão Macro & Avaliação".</p></div>`}
    </div>`;
  }

  /** AVR ENCONTRADO — EM ELABORAÇÃO / EM ANÁLISE / APROVADO…: o Engenheiro vê e analisa; só a válida alimenta a Vida Residual. */
  private renderRelatedAvrs(): string {
    const list = this.assessmentCtx?.relatedAvrs || [];
    if (list.length === 0) return "";
    return `<div class="ws-assess" data-section="avr">
      <div class="ws-assess-head"><h3>AVR encontrado para a TAG (${list.length})</h3><small>Consulta da Engenharia: qualquer estado. Somente AVR aprovada/validada tecnicamente alimenta a Vida Residual automaticamente.</small></div>
      ${list.map(a => `<div class="ws-assess-current" style="display:flex; justify-content:space-between; align-items:center; gap:10px; flex-wrap:wrap;" data-item="${this.esc(a.avrNumber)}">
        <span><b>AVR ENCONTRADO — ${this.esc(a.statusLabel.toUpperCase())}</b> · Nº: <b>${this.esc(a.avrNumber)}</b> · Rev. ${this.esc(a.revision)} · ${a.validForResidualLife ? "<b>utilizado para a Vida Residual</b>" : "<i>este AVR ainda não está aprovado e não será utilizado automaticamente para determinar a Vida Residual</i>"}</span>
        <span><button class="ws-btn primary btn-ndt-route" data-route="${this.esc(a.reviewRoute)}">ANALISAR AVR</button> <button class="ws-btn btn-ndt-route" data-route="${this.esc(a.route)}">Abrir AVR</button></span>
      </div>`).join("")}
    </div>`;
  }

  private mapRiStatus(status: string): string {
    const map: Record<string, string> = {
      'DRAFT': 'RASCUNHO',
      'IN_ELABORATION': 'EM ELABORAÇÃO',
      'FINALIZED_BY_INSPECTOR': 'FINALIZADO PELO INSPETOR',
      'SUBMITTED_TO_ENGINEERING': 'AGUARDANDO ANÁLISE',
      'UNDER_ENGINEERING_REVIEW': 'EM ANÁLISE',
      'RETURNED': 'DEVOLVIDO',
      'IN_CORRECTION': 'EM CORREÇÃO',
      'APPROVED': 'CONFORME',
      'AWAITING_SIGNATURE': 'AGUARDANDO ASSINATURA',
      'SIGNED': 'ASSINADO',
      'COMPLETED': 'CONCLUÍDO'
    };
    return map[status] || status;
  }

  private getRiStatusColors(status: string): { bg: string; text: string } {
    switch (status) {
      case 'SUBMITTED_TO_ENGINEERING':
      case 'UNDER_ENGINEERING_REVIEW': return { bg: 'var(--color-primary)', text: 'var(--color-primary-text)' };
      case 'RETURNED':
      case 'IN_CORRECTION': return { bg: '#7f1d1d', text: '#FFFFFF' };
      case 'APPROVED': return { bg: '#166534', text: '#FFFFFF' };
      case 'AWAITING_SIGNATURE': return { bg: '#5C5C5C', text: '#EEEEEE' };
      case 'SIGNED':
      case 'COMPLETED': return { bg: '#10b981', text: 'var(--color-primary-text)' };
      default: return { bg: 'var(--color-graphite-500)', text: '#FFFFFF' };
    }
  }

  private renderRiTab() {
    if (!this.workspaceData) return '<div>Erro ao carregar dados do relatório.</div>';
    const { report } = this.workspaceData;
    const ctx = contextStore.get();
    const perms = ctx?.permissions;
    const has = (p: string) => perms?.has(p) ?? false;

    const documentNumber = report.reportNumber && !report.reportNumber.includes('RI-NOVO')
      ? report.reportNumber
      : (this.riPdfSnapshot?.documentNumber && this.riPdfSnapshot.documentNumber !== report.id ? this.riPdfSnapshot.documentNumber : null);

    const responsibleName = this.displayName((report as any).validatedByUserId || report.approvedByPlh || report.engineerPlhId);

    // Gates for "Validar Relatório" (Fase 14) — never hide the button, only
    // disable it with the real reason.
    const pendingCount = this.checklistComponents.filter(c => c.result === 'PENDING' || c.status === 'PENDING').length;
    const minutaStatus = this.minutaContext?.minuta?.status;
    const minutaBlocking = minutaStatus && minutaStatus !== 'CONFORME' && minutaStatus !== 'APROVADA' && minutaStatus !== 'ANÁLISE CONCLUÍDA';
    const validateReasons: string[] = [];
    if (pendingCount > 0) validateReasons.push(`${pendingCount} item(ns) do Checklist ainda pendente(s)`);
    if (minutaBlocking) validateReasons.push('a Minuta desta inspeção ainda não está CONFORME');
    const canValidateReport = validateReasons.length === 0;

    let actionBar = '';
    if (report.status === 'SUBMITTED_TO_ENGINEERING' || report.status === 'UNDER_ENGINEERING_REVIEW') {
      const btns: string[] = [];
      if (has(PermissionCode.IntegrityReportEdit)) {
        btns.push(`<button id="btn-ri-edit" style="background: var(--color-surface-secondary); color: var(--color-text-primary); border: 1px solid var(--color-graphite-300); padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer;">Editar Relatório</button>`);
      }
      if (has(PermissionCode.IntegrityReportValidate)) {
        btns.push(`<button id="btn-ri-validate" ${canValidateReport ? '' : 'disabled'} title="${canValidateReport ? '' : `Existem pendências: ${validateReasons.join('; ')}.`}" style="background: ${canValidateReport ? 'var(--status-success-solid)' : 'var(--color-bg-secondary)'}; color: ${canValidateReport ? 'var(--color-primary-text)' : 'var(--color-graphite-500)'}; border: 1px solid ${canValidateReport ? 'var(--status-success-solid)' : 'var(--color-graphite-300)'}; padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: ${canValidateReport ? 'pointer' : 'not-allowed'};">Validar Relatório</button>`);
        if (!canValidateReport) {
          btns.push(`<span style="font-size: 0.8125rem; color: var(--status-danger-text); align-self: center;">Existem pendências: ${validateReasons.join('; ')}.</span>`);
        }
      }
      actionBar = btns.join('');
    } else if (report.status === 'APPROVED') {
      const hasApprove = has(PermissionCode.IntegrityReportApprove) && has(PermissionCode.IntegrityReportSign);
      const btns: string[] = [];
      if (hasApprove) {
        btns.push(`<button type="button" id="btn-ri-approve" style="background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8); border: none; padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer;">Aprovar e Assinar (PLH)</button>`);
      }
      if (has(PermissionCode.EngineeringRequestCorrection)) {
        btns.push(`<button type="button" id="btn-ri-return" style="background: var(--color-surface-secondary); color: var(--status-danger-text, #dc2626); border: 1px solid var(--status-danger-border, #fca5a5); padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer;">Mandar para Correção</button>`);
      }
      actionBar = btns.join(' ');
    } else if (report.status === 'AWAITING_SIGNATURE') {
      const btns: string[] = [];
      btns.push(`<span style="background: var(--status-warning-bg); color: var(--status-warning-text); border: 1px solid var(--status-warning-border); padding: 8px 16px; border-radius: 6px; font-weight: 600;">Aguardando Assinatura</span>`);
      if (has(PermissionCode.EngineeringRequestCorrection)) {
        btns.push(`<button type="button" id="btn-ri-return" style="background: var(--color-surface-secondary); color: var(--status-danger-text, #dc2626); border: 1px solid var(--status-danger-border, #fca5a5); padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer;" title="Revogar aprovação e devolver para correção do Inspetor">Mandar para Correção</button>`);
      }
      actionBar = btns.join(' ');
    } else if (report.status === 'SIGNED' || report.status === 'COMPLETED') {
      actionBar = `<span style="background: var(--status-success-bg); color: var(--status-success-text); border: 1px solid var(--status-success-border); padding: 8px 16px; border-radius: 6px; font-weight: 600;">Documento assinado — somente consulta</span>`;
    }

    const statusColors = this.getRiStatusColors(report.status);

    return `
      <div style="display: flex; flex-direction: column; gap: 20px;">
        <div style="background: var(--color-surface-primary); border: 1px solid var(--color-graphite-200); border-radius: 8px; padding: 20px; display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 12px;">
          <div style="display: flex; flex-direction: column; gap: 4px;">
            <div style="display: flex; align-items: center; gap: 12px; flex-wrap: wrap;">
              <span style="font-size: 0.75rem; color: var(--color-text-secondary); text-transform: uppercase; font-weight: 600;">RELATÓRIO:</span>
              <span style="font-size: 1.125rem; font-weight: 700; color: var(--color-text-primary);">${documentNumber || 'Nº ainda não gerado'}</span>
              <span style="background: var(--color-surface-secondary); color: var(--color-text-primary); border: 1px solid var(--color-graphite-300); padding: 4px 8px; border-radius: 4px; font-size: 0.8125rem; font-weight: 600;">Rev ${report.revision || '00'}</span>
              <span style="background: ${statusColors.bg}; color: ${statusColors.text}; padding: 4px 8px; border-radius: 4px; font-size: 0.8125rem; font-weight: 600;">STATUS DO RI: ${this.mapRiStatus(report.status)}</span>
            </div>
            <p style="color: var(--color-text-secondary); font-size: 0.8125rem; margin: 4px 0 0 0;">Responsável: ${responsibleName !== '—' ? responsibleName : 'Não iniciado'}</p>
          </div>
          <div style="display: flex; gap: 12px; align-items: center; flex-wrap: wrap;">${actionBar}</div>
        </div>

        <div style="height: 720px; border: 1px solid var(--color-graphite-200); border-radius: 8px; overflow: hidden;">
          ${this.riPdfSnapshot
            ? `<app-pdf-preview id="ri-pdf-preview"></app-pdf-preview>`
            : `<div style="height: 100%; display: flex; align-items: center; justify-content: center; color: var(--color-text-secondary);">Carregando Relatório de Integridade...</div>`}
        </div>
      </div>
    `;
  }

  /**
   * REVISÃO IA do Relatório de Integridade — consulta para a Engenharia/PLH.
   * A IA não substitui a análise do PLH: mostra o status e os achados.
   */
  private renderAiReviewTab() {
    const report: any = this.workspaceData?.report;
    if (!report?.id) return `<div class="ws-assess-msg">Relatório de Integridade não carregado.</div>`;
    return `<ai-review-panel data-type="INTEGRITY" data-id="${this.esc(String(report.id))}" data-mode="consult"></ai-review-panel>`;
  }

  /** Cabeçalho da MINUTA TÉCNICA (fonte única): gerar, atualizar pela fonte (hash), salvar rascunho, validar, solicitar correção, visualizar, histórico. */
  private renderMinutaTecnicaHead(): string {
    const mt = this.minutaTecnica;
    const report: any = this.workspaceData?.report;
    if (!mt || !report) return "";
    const canCreate = contextStore.hasPermission(PermissionCode.EngineeringMinuteCreate);
    const canEdit = contextStore.hasPermission(PermissionCode.EngineeringMinuteEdit);
    if (!mt.minuta) {
      return `
        <div class="ws-assess" data-section="minuta">
          <div class="ws-assess-head"><h3>Minuta Técnica</h3><small>Ainda não gerada. A Minuta usa os dados já existentes do relatório (cliente, projeto, TAG, OS, Sub-OS, recomendações, condição, Vida Residual) — nada é redigitado.</small></div>
          <div class="ws-assess-actions">${canCreate ? `<button id="btn-generate-minuta" class="ws-btn primary">GERAR MINUTA</button>` : `<span class="ws-assess-msg err">Sem permissão engineering.minute_create.</span>`}</div>
        </div>`;
    }
    const d = mt.diff;
    const banner = mt.updateNeeded
      ? `<div class="ws-assess-msg err" style="display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap;">
          <span><b>ATUALIZAÇÃO DA MINUTA NECESSÁRIA</b> — NOVAS: ${d.newCount} · ALTERADAS: ${d.modifiedCount} · REMOVIDAS: ${d.removedCount} · PENDENTES: ${d.pendingCount}${d.otherChanges.length ? ` · ${this.esc(d.otherChanges.join(", "))}` : ""}</span>
          ${canEdit ? `<button id="btn-update-minuta" class="ws-btn primary">GERAR ATUALIZAÇÃO DA MINUTA</button>` : ""}
        </div>`
      : `<div class="ws-assess-current">Minuta sincronizada com a fonte (hash ${this.esc((mt.minuta.lastSourceHash || mt.source.sourceHash).slice(0, 12))}…) — NOVAS: 0 · ALTERADAS: 0 · PENDENTES: 0.</div>`;
    const changed = d.items.filter(i => i.type !== "INALTERADA");
    const changes = changed.length > 0 && mt.updateNeeded ? `<ul style="margin:6px 0 0 0; padding-left:18px; font-size:0.8rem; color: var(--color-text-secondary);">${changed.map(i => `<li><b>${this.esc(i.type)}</b> ${this.esc(i.id)}${i.fields.length ? ` — ${this.esc(i.fields.join(", "))}` : ""}</li>`).join("")}</ul>` : "";
    const reasons = mt.validationReasons;
    const canValidate = reasons.length === 0 && contextStore.hasPermission(PermissionCode.EngineeringValidateMinuta);
    return `
      <div class="ws-assess" data-section="minuta">
        <div class="ws-assess-head"><h3>Minuta Técnica — ${this.esc(mt.minuta.trackingNumber)} · Rev. ${this.esc(mt.minuta.currentRevision)}</h3><small>Fonte única da inspeção. Status: ${this.esc(this.getMinutaStatusLabel(mt.minuta.status))}.</small></div>
        ${banner}${changes}
        <label class="ws-field"><span>Observações da Engenharia (rascunho)</span><textarea id="minuta-notes" rows="3" style="padding:8px 10px; border:1px solid var(--color-graphite-300); border-radius:6px; font-size:0.85rem; font-family:inherit;" ${canEdit ? "" : "disabled"}>${this.esc(this.minutaNotes)}</textarea></label>
        <div class="ws-assess-actions">
          ${canEdit ? `<button id="btn-minuta-save-draft" class="ws-btn">SALVAR RASCUNHO</button>` : ""}
          ${canEdit && mt.minuta.status === "EM ANÁLISE" ? `<button id="btn-minuta-edit-mode-head" class="ws-btn">${this.minutaEditMode ? "EDITANDO…" : "EDITAR"}</button>` : ""}
          <button id="btn-minuta-validate-head" class="ws-btn primary" ${canValidate ? "" : "disabled"} title="${this.esc(canValidate ? "" : `Não é possível validar: ${reasons.join("; ")}.`)}">VALIDAR MINUTA</button>
          ${contextStore.hasPermission(PermissionCode.EngineeringRequestCorrection) ? `<button id="btn-minuta-request-correction" class="ws-btn">SOLICITAR CORREÇÃO</button>` : ""}
          <button id="btn-minuta-preview" class="ws-btn">${this.minutaPreviewOpen ? "FECHAR VISUALIZAÇÃO" : "VISUALIZAR"}</button>
          <button id="btn-minuta-history-head" class="ws-btn">HISTÓRICO</button>
        </div>
        ${!canValidate && reasons.length > 0 ? `
          <div style="background: var(--status-danger-bg, #fef2f2); border: 1px solid var(--status-danger-border, #fca5a5); border-radius: 6px; padding: 10px 14px; margin-top: 8px;">
            <div style="font-weight: 700; font-size: 0.8125rem; color: var(--status-danger-text, #991b1b); margin-bottom: 4px;">PENDÊNCIAS IMPEDITIVAS:</div>
            <ul style="margin: 0; padding-left: 18px; font-size: 0.8rem; color: var(--status-danger-text, #991b1b);">
              ${reasons.map(r => `<li>🔴 ${this.esc(r)}</li>`).join("")}
            </ul>
          </div>
        ` : ""}
        ${mt.warnings && mt.warnings.length > 0 ? `
          <div style="background: var(--status-warning-bg, #fefce8); border: 1px solid var(--status-warning-border, #fde047); border-radius: 6px; padding: 10px 14px; margin-top: 8px;">
            <div style="font-weight: 700; font-size: 0.8125rem; color: var(--status-warning-text, #854d0e); margin-bottom: 4px;">AVISOS (NÃO IMPEDITIVOS):</div>
            <ul style="margin: 0; padding-left: 18px; font-size: 0.8rem; color: var(--status-warning-text, #854d0e);">
              ${mt.warnings.map(w => `<li>🟡 ${this.esc(w)}</li>`).join("")}
            </ul>
          </div>
        ` : ""}
        ${this.minutaPreviewOpen && this.minutaPdfSnapshot ? `<div style="height: 640px; border: 1px solid var(--color-graphite-200); border-radius: 8px; overflow: hidden;"><app-pdf-preview id="minuta-pdf-preview-head" style="height:100%; display:block;"></app-pdf-preview></div>` : ""}
      </div>`;
  }

  private renderMinutaTab() {
    if (this.isMinutaLoading) {
      return `<div style="padding: 24px;">Carregando dados da Minuta...</div>`;
    }
    if (!this.minutaContext) {
      return `
        <div style="display:flex; flex-direction:column; gap:16px;">
          ${this.renderMinutaTecnicaHead()}
          ${this.minutaTecnica?.minuta ? "" : `<div style="padding: 24px; text-align: center;">
            <h2 style="color: var(--color-text-muted); font-size: 1.25rem;">Nenhuma Minuta gerada para esta inspeção</h2>
            <p style="color: var(--color-text-secondary); font-size: 0.875rem;">Use GERAR MINUTA acima — a Minuta Técnica é única e usa os dados já existentes do relatório.</p>
          </div>`}
        </div>`;
    }

    const { minuta, recommendations, corrections, pendencySummary } = this.minutaContext;

    const actionButtons = this.renderMinutaActionBar(minuta, pendencySummary);

    const responsibleLines: string[] = [];
    if (minuta.analysisStartedBy) {
      const when = minuta.analysisStartedAt ? new Date(minuta.analysisStartedAt).toLocaleString('pt-BR') : '—';
      responsibleLines.push(`<p style="color: var(--color-text-secondary); font-size: 0.875rem; margin: 0;">Responsável pela análise: <strong>${this.displayName(minuta.analysisStartedBy)}</strong> · Iniciada em: ${when}</p>`);
    } else {
      responsibleLines.push(`<p style="color: var(--color-text-secondary); font-size: 0.875rem; margin: 0;">Responsável pela análise: Não iniciado</p>`);
    }
    if (minuta.validatedByUserId) {
      const when = minuta.validatedAt ? new Date(minuta.validatedAt).toLocaleString('pt-BR') : '—';
      responsibleLines.push(`<p style="color: var(--color-text-secondary); font-size: 0.875rem; margin: 0;">Validada como Conforme por: <strong>${this.displayName(minuta.validatedByUserId)}</strong> · Data: ${when}</p>`);
    }
    if (minuta.approvedBy) {
      const when = minuta.approvedAt ? new Date(minuta.approvedAt).toLocaleString('pt-BR') : '—';
      responsibleLines.push(`<p style="color: var(--color-text-secondary); font-size: 0.875rem; margin: 0;">Aprovada e assinada (PLH) por: <strong>${this.displayName(minuta.approvedBy)}</strong> · Data: ${when}</p>`);
    }

    return `
      <div style="display: flex; flex-direction: column; gap: 24px; height: 100%;">
        ${this.renderMinutaTecnicaHead()}
        <div style="background: var(--color-surface-primary); border: 1px solid var(--color-graphite-200); border-radius: 8px; padding: 24px; display: flex; justify-content: space-between; align-items: flex-start; box-shadow: 0 1px 3px rgba(0,0,0,0.05);">
          <div style="display: flex; flex-direction: column; gap: 4px;">
            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 4px;">
              <h2 style="color: var(--color-text-primary); font-size: 1.5rem; font-weight: 700; margin: 0;">Minuta: ${minuta.trackingNumber || 'Nova'}</h2>
              <span style="background: var(--color-surface-secondary); color: var(--color-text-primary); border: 1px solid var(--color-graphite-300); padding: 4px 8px; border-radius: 4px; font-size: 0.875rem; font-weight: 600;">Rev ${minuta.currentRevision}</span>
              ${(() => { const c = this.getMinutaStatusColors(minuta.status); return `<span style="background: ${c.bg}; color: ${c.text}; padding: 4px 8px; border-radius: 4px; font-size: 0.875rem; font-weight: 600;">${this.getMinutaStatusLabel(minuta.status)}</span>`; })()}
            </div>
            ${responsibleLines.join('')}
          </div>
          <div style="display: flex; gap: 12px;">
            ${actionButtons}
          </div>
        </div>

        <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px;">
          <div style="background: var(--color-surface-primary); border: 1px solid var(--color-graphite-200); border-radius: 8px; padding: 16px; text-align: center;">
            <span style="font-size: 1.5rem; font-weight: 700; color: var(--ia-primary);">${pendencySummary.totalCorrections}</span>
            <p style="margin: 4px 0 0 0; font-size: 0.75rem; color: var(--color-text-secondary); text-transform: uppercase;">Total Solicitações</p>
          </div>
          <div style="background: var(--color-surface-primary); border: 1px solid var(--color-graphite-200); border-radius: 8px; padding: 16px; text-align: center;">
            <span style="font-size: 1.5rem; font-weight: 700; color: var(--status-danger-solid);">${pendencySummary.awaitingInspector}</span>
            <p style="margin: 4px 0 0 0; font-size: 0.75rem; color: var(--color-text-secondary); text-transform: uppercase;">Aguardando Inspetor</p>
          </div>
          <div style="background: var(--color-surface-primary); border: 1px solid var(--color-graphite-200); border-radius: 8px; padding: 16px; text-align: center;">
            <span style="font-size: 1.5rem; font-weight: 700; color: var(--status-warning-text);">${pendencySummary.awaitingValidation}</span>
            <p style="margin: 4px 0 0 0; font-size: 0.75rem; color: var(--color-text-secondary); text-transform: uppercase;">Para Validar</p>
          </div>
          <div style="background: var(--color-surface-primary); border: 1px solid var(--color-graphite-200); border-radius: 8px; padding: 16px; text-align: center;">
            <span style="font-size: 1.5rem; font-weight: 700; color: var(--status-success-text);">${pendencySummary.validated}</span>
            <p style="margin: 4px 0 0 0; font-size: 0.75rem; color: var(--color-text-secondary); text-transform: uppercase;">Validadas</p>
          </div>
        </div>

        <div id="minuta-recommendations-section" style="display: flex; flex-direction: column; gap: 16px;">
          <h3 style="margin: 0; color: var(--color-text-muted); font-size: 1.125rem;">Recomendações Técnicas</h3>
          ${this.minutaEditMode && minuta.status === 'EM ANÁLISE' ? `
            <div style="background: var(--status-warning-bg); border: 1px solid var(--status-warning-border); border-radius: 8px; padding: 12px 16px; display: flex; justify-content: space-between; align-items: center; gap: 12px;">
              <p style="margin: 0; font-size: 0.8125rem; color: var(--status-warning-text);">
                <strong>MODO DE EDIÇÃO ATIVO</strong> — clique em "Editar" em qualquer recomendação abaixo. As alterações são aplicadas diretamente ao Checklist de Inspeção e refletem automaticamente na Minuta; não é uma cópia independente.
              </p>
              <button id="btn-minuta-edit-mode-exit" style="background: none; border: 1px solid var(--status-warning-border); color: var(--status-warning-text); padding: 4px 12px; border-radius: 4px; cursor: pointer; font-size: 0.75rem; white-space: nowrap;">Sair do Modo de Edição</button>
            </div>
          ` : ''}
          ${recommendations.length === 0 ? `<p>Nenhuma recomendação associada.</p>` : recommendations.map((rec: any) => this.renderRecommendationCard(rec)).join('')}
        </div>

        ${corrections.length > 0 ? `
          <div style="display: flex; flex-direction: column; gap: 12px;">
            <h3 style="margin: 0; color: var(--color-text-muted); font-size: 1.125rem;">Solicitações de Correção</h3>
            ${this.renderCorrectionsTable(corrections, recommendations)}
          </div>
        ` : ''}

        <div style="display: flex; flex-direction: column; gap: 12px;">
          <h3 style="margin: 0; color: var(--color-text-muted); font-size: 1.125rem;">Documento (PDF)</h3>
          ${this.minutaPdfSnapshot ? `
            <div style="height: 640px; border: 1px solid var(--color-graphite-200); border-radius: 8px; overflow: hidden;">
              <app-pdf-preview id="minuta-pdf-preview"></app-pdf-preview>
            </div>
          ` : `
            <p style="color: var(--color-text-secondary); font-size: 0.875rem;">Nenhum documento gerado ainda para esta Minuta.</p>
          `}
        </div>
      </div>
    `;
  }

  // Bloco 8, Parte C/D: the previous switch-based action area either showed
  // a single button OR a plain text message, never both — so "Validar
  // Minuta" (and every other primary action) simply disappeared whenever a
  // condition wasn't met, instead of appearing disabled with a reason. This
  // renders a consistent action bar for every Minuta status, always
  // reflecting the acting user's real effective permissions (never a
  // hardcoded allow-list for a specific person).
  private renderMinutaActionBar(minuta: any, pendencySummary: any): string {
    const ctx = contextStore.get();
    const perms = ctx?.permissions;
    const has = (p: string) => perms?.has(p) ?? false;

    const btnStyle = (variant: 'primary' | 'success' | 'danger' | 'ghost', disabled: boolean) => {
      const palette: Record<string, { bg: string; color: string; border: string }> = {
        primary: { bg: 'var(--color-primary)', color: 'var(--color-primary-text)', border: 'var(--color-primary)' },
        success: { bg: 'var(--status-success-solid)', color: 'white', border: 'var(--status-success-solid)' },
        danger: { bg: 'var(--status-danger-bg)', color: 'var(--status-danger-text)', border: 'var(--status-danger-solid)' },
        ghost: { bg: 'var(--color-surface-primary)', color: 'var(--color-text-muted)', border: 'var(--color-graphite-300)' }
      };
      const p = palette[variant]!;
      if (disabled) {
        return `background: var(--color-bg-secondary); color: var(--color-graphite-500); border: 1px solid var(--color-graphite-300); padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: not-allowed;`;
      }
      return `background: ${p.bg}; color: ${p.color}; border: 1px solid ${p.border}; padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer;`;
    };

    const hint = (text: string) => `<span style="font-size: 0.8125rem; color: var(--status-danger-text); font-weight: 500; align-self: center;">${text}</span>`;
    const buttons: string[] = [];

    const hasEdit = has(PermissionCode.EngineeringEditRecommendation);
    const hasReturn = has(PermissionCode.MinutaReturnForCorrection);

    switch (minuta.status) {
      case 'EMITIDA':
      case 'AGUARDANDO ANÁLISE':
      case 'EM ANÁLISE': {
        const hasRequestCorrection = has(PermissionCode.EngineeringRequestCorrection) && has(PermissionCode.EngineeringDelegateToInspector);
        const hasValidate = has(PermissionCode.EngineeringValidateMinuta);

        if (hasEdit) {
          buttons.push(`<button type="button" id="btn-minuta-edit-mode" style="${btnStyle('ghost', false)}">${this.minutaEditMode ? 'Editando Minuta…' : 'Editar Minuta'}</button>`);
        }
        if (hasRequestCorrection) {
          buttons.push(`<button type="button" id="btn-minuta-goto-correction" style="${btnStyle('danger', false)}">Solicitar Correção / Enviar ao Inspetor</button>`);
        }

        const { enabled: canValidate, reasons } = computeValidateMinutaState(pendencySummary);
        const blocked = !canValidate;

        if (hasValidate) {
          buttons.push(`<button type="button" id="btn-minuta-validate" ${blocked ? 'disabled' : ''} title="${blocked ? `Existem ${reasons.join('; ')}.` : ''}" style="${btnStyle('success', blocked)}">Validar Minuta</button>`);
          if (blocked) {
            buttons.push(hint(`Existem ${reasons.join('; ')}.`));
          }
        }
        if (hasReturn) {
          buttons.push(`<button type="button" id="btn-minuta-return" style="${btnStyle('danger', false)}">Reprovar / Devolver</button>`);
        }
        break;
      }
      case 'AGUARDANDO INSPETOR':
      case 'CORREÇÃO NECESSÁRIA':
        buttons.push(hint(`Aguardando o Inspetor corrigir e reenviar ${pendencySummary.awaitingInspector} recomendação(ões).`));
        break;
      case 'AGUARDANDO VALIDAÇÃO': {
        buttons.push(`<span style="background: var(--status-warning-bg); color: var(--status-warning-text); border: 1px solid var(--status-warning-border); padding: 8px 16px; border-radius: 6px; font-weight: 600;">Aguardando sua validação</span>`);
        if (hasReturn) {
          buttons.push(`<button type="button" id="btn-minuta-return" style="${btnStyle('danger', false)}">Reprovar / Devolver</button>`);
        }
        break;
      }
      case 'CONFORME':
      case 'ANÁLISE CONCLUÍDA': { // legado, pré-Bloco 6 — tratado como CONFORME
        const hasApprove = has(PermissionCode.EngineeringApproveMinuta) && has(PermissionCode.EngineeringSignMinuta);
        buttons.push(hasApprove
          ? `<button type="button" id="btn-minuta-approve" style="${btnStyle('primary', false)}">Aprovar e Assinar (PLH)</button>`
          : hint('Você não possui permissão para aprovar/assinar como PLH.'));
        if (hasEdit) {
          buttons.push(`<button type="button" disabled title="A Minuta está validada. Gere nova revisão para realizar alterações." style="${btnStyle('ghost', true)}">Editar Minuta</button>`);
        }
        break;
      }
      case 'APROVADA':
        buttons.push(`<span style="background: var(--status-success-bg); color: var(--status-success-text); border: 1px solid var(--status-success-border); padding: 8px 16px; border-radius: 6px; font-weight: 600;">Documento aprovado e assinado — somente consulta</span>`);
        if (hasEdit) {
          buttons.push(`<button type="button" disabled title="A Minuta está aprovada e assinada. Gere nova revisão para realizar alterações." style="${btnStyle('ghost', true)}">Editar Minuta</button>`);
        }
        break;
    }

    buttons.push(`<button type="button" id="btn-minuta-history" style="${btnStyle('ghost', false)}">Histórico</button>`);

    return `<div style="display: flex; gap: 12px; align-items: center; flex-wrap: wrap; justify-content: flex-end;">${buttons.join('')}</div>`;
  }

  private renderMinutaHistoryModal(): string {
    if (!this.minutaContext) return '';
    const revisions = [...(this.minutaContext.revisions || [])].sort((a: any, b: any) => (a.revisionNumber || '').localeCompare(b.revisionNumber || ''));
    return `
      <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 100; display: flex; justify-content: center; align-items: center;">
        <div style="background: var(--color-surface-primary); padding: 24px; border-radius: 8px; width: 620px; max-height: 80vh; overflow-y: auto;">
          <h3 style="margin-top: 0; color: var(--color-text-primary);">Histórico de Revisões — ${this.minutaContext.minuta.trackingNumber || 'Minuta'}</h3>
          ${revisions.length === 0 ? `<p style="color: var(--color-text-secondary);">Nenhuma revisão registrada ainda.</p>` : `
            <table style="width: 100%; border-collapse: collapse; font-size: 0.8125rem;">
              <thead>
                <tr style="background: var(--color-bg-secondary);">
                  <th style="padding: 8px; text-align: left;">Revisão</th>
                  <th style="padding: 8px; text-align: left;">Status</th>
                  <th style="padding: 8px; text-align: left;">Motivo</th>
                  <th style="padding: 8px; text-align: left;">Criada em</th>
                </tr>
              </thead>
              <tbody>
                ${revisions.map((r: any) => `
                  <tr>
                    <td style="padding: 8px; border-bottom: 1px solid var(--color-graphite-200); font-weight: 600;">${r.revisionNumber}</td>
                    <td style="padding: 8px; border-bottom: 1px solid var(--color-graphite-200);">${r.status}</td>
                    <td style="padding: 8px; border-bottom: 1px solid var(--color-graphite-200);">${r.correctionReason || '—'}</td>
                    <td style="padding: 8px; border-bottom: 1px solid var(--color-graphite-200); color: var(--color-text-secondary);">${r.createdAt ? new Date(r.createdAt).toLocaleString('pt-BR') : '—'}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          `}
          <div style="display: flex; justify-content: flex-end; margin-top: 16px;">
            <button id="btn-close-minuta-history" style="padding: 8px 16px; border: 1px solid var(--color-graphite-300); background: var(--color-surface-primary); cursor: pointer; border-radius: 4px;">Fechar</button>
          </div>
        </div>
      </div>
    `;
  }

  private renderCorrectionsTable(corrections: readonly any[], recommendations: readonly any[]) {
    const rows = corrections.map(c => {
      const rec = recommendations.find((r: any) => r.id === c.recommendationId);
      const recLabel = rec ? (rec.scope || rec.recommendationNumber || rec.zr || rec.id) : c.recommendationId;
      let responsible = '—';
      if (['PENDENTE', 'EM CORREÇÃO', 'DEVOLVIDA'].includes(c.status)) {
        responsible = 'Aguardando Inspetor';
      } else if (c.status === 'AGUARDANDO VALIDAÇÃO' || c.status === 'CORRIGIDA') {
        responsible = this.displayName(c.correctedBy) + ' (corrigiu)';
      } else if (c.status === 'VALIDADA') {
        responsible = this.displayName(c.validatedBy) + ' (validou)';
      }
      const lastDate = c.updatedAt ? new Date(c.updatedAt).toLocaleString('pt-BR') : (c.createdAt ? new Date(c.createdAt).toLocaleString('pt-BR') : '—');
      const before = c.previousState?.scope;
      const after = c.newState?.scope;
      const beforeAfter = (before || after) ? `
        <div style="margin-top: 6px; font-size: 0.75rem;">
          ${before ? `<div style="color: var(--status-danger-text);"><strong>Antes:</strong> ${before}</div>` : ''}
          ${after ? `<div style="color: var(--status-success-text); margin-top: 2px;"><strong>Depois:</strong> ${after}</div>` : ''}
        </div>
      ` : '';

      return `
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid var(--color-graphite-200); font-size: 0.8125rem;">${c.visualIndex || '—'}</td>
          <td style="padding: 8px; border-bottom: 1px solid var(--color-graphite-200); font-size: 0.8125rem; max-width: 220px;">${recLabel}${beforeAfter}</td>
          <td style="padding: 8px; border-bottom: 1px solid var(--color-graphite-200); font-size: 0.8125rem;">${c.reason || '—'}</td>
          <td style="padding: 8px; border-bottom: 1px solid var(--color-graphite-200); font-size: 0.8125rem;">${this.displayName(c.requesterId)}</td>
          <td style="padding: 8px; border-bottom: 1px solid var(--color-graphite-200); font-size: 0.8125rem;">${responsible}</td>
          <td style="padding: 8px; border-bottom: 1px solid var(--color-graphite-200); font-size: 0.8125rem; font-weight: 600;">${c.status}</td>
          <td style="padding: 8px; border-bottom: 1px solid var(--color-graphite-200); font-size: 0.75rem; color: var(--color-text-secondary);">${lastDate}</td>
        </tr>
      `;
    }).join('');

    return `
      <div style="overflow-x: auto; background: var(--color-surface-primary); border: 1px solid var(--color-graphite-200); border-radius: 8px;">
        <table style="width: 100%; border-collapse: collapse;">
          <thead>
            <tr style="background: var(--color-bg-secondary);">
              <th style="padding: 8px; text-align: left; font-size: 0.75rem; text-transform: uppercase; color: var(--color-text-secondary);">Item</th>
              <th style="padding: 8px; text-align: left; font-size: 0.75rem; text-transform: uppercase; color: var(--color-text-secondary);">Recomendação</th>
              <th style="padding: 8px; text-align: left; font-size: 0.75rem; text-transform: uppercase; color: var(--color-text-secondary);">Motivo</th>
              <th style="padding: 8px; text-align: left; font-size: 0.75rem; text-transform: uppercase; color: var(--color-text-secondary);">Solicitado por</th>
              <th style="padding: 8px; text-align: left; font-size: 0.75rem; text-transform: uppercase; color: var(--color-text-secondary);">Responsável</th>
              <th style="padding: 8px; text-align: left; font-size: 0.75rem; text-transform: uppercase; color: var(--color-text-secondary);">Status</th>
              <th style="padding: 8px; text-align: left; font-size: 0.75rem; text-transform: uppercase; color: var(--color-text-secondary);">Data</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    `;
  }

  private renderRecommendationCard(rec: any) {
    const isAnalyzing = this.minutaContext?.minuta.status === 'EM ANÁLISE';
    const correction = this.minutaContext?.corrections.find(c => c.recommendationId === rec.id);
    const hasPendingCorrection = correction && ['PENDENTE', 'EM CORREÇÃO', 'DEVOLVIDA'].includes(correction.status);
    const hasValidationPending = correction && correction.status === 'AGUARDANDO VALIDAÇÃO';
    
    const editHighlight = this.minutaEditMode && isAnalyzing;
    return `
      <div style="background: var(--color-surface-primary); border: 1px solid ${hasValidationPending ? 'var(--status-warning-border)' : (editHighlight ? 'var(--color-primary)' : 'var(--color-graphite-200)')}; border-radius: 8px; padding: 16px; box-shadow: 0 1px 2px rgba(0,0,0,0.05);">
        <div style="display: flex; justify-content: space-between; align-items: flex-start;">
          <div>
            <div style="display: flex; gap: 8px; margin-bottom: 8px; align-items: center; flex-wrap: wrap;">
              ${isPendingZr(rec) ? `
                <span style="background: var(--status-warning-bg, #5C5C5C); padding: 4px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 700; color: var(--status-warning-text, #EEEEEE);" title="Recomendação sem numeração ZR">⚠ ZR: ${ZR_PENDING_LABEL}</span>
              ` : `
                <span style="background: var(--color-surface-secondary); padding: 4px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; color: var(--color-text-primary);">ZR: ${this.esc(formatZrDisplay(rec))}</span>
              `}
              <span style="background: var(--color-surface-secondary); padding: 4px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; color: var(--color-text-primary);">${rec.discipline || '-'}</span>
              ${!isPendingZr(rec) && this.canEditZrNumber() ? `
                <button class="btn-edit-zr" data-id="${rec.id}" style="background: none; border: 1px solid var(--color-graphite-300); color: var(--color-text-muted); padding: 3px 9px; border-radius: 4px; cursor: pointer; font-size: 0.72rem;">Editar Nº ZR</button>
              ` : ''}
              ${!isPendingZr(rec) && this.canRemoveZrNumber() ? `
                <button class="btn-remove-zr" data-id="${rec.id}" style="background: none; border: 1px solid var(--status-danger-solid); color: var(--status-danger-solid); padding: 3px 9px; border-radius: 4px; cursor: pointer; font-size: 0.72rem;">Remover Nº ZR</button>
              ` : ''}
            </div>
            <p style="margin: 0 0 8px 0; color: var(--color-text-primary); font-size: 0.9375rem;">${rec.scope || '-'}</p>
          </div>
          <div style="display: flex; gap: 8px; flex-direction: column; align-items: flex-end;">
            ${isAnalyzing ? `
              <button class="btn-edit-rec" data-id="${rec.id}" style="background: ${editHighlight ? 'var(--color-primary)' : 'none'}; color: ${editHighlight ? 'var(--color-primary-text)' : 'inherit'}; border: 1px solid var(--color-primary); padding: 4px 12px; border-radius: 4px; cursor: pointer; font-size: 0.75rem; font-weight: ${editHighlight ? '600' : '400'};">Editar</button>
              <button class="btn-req-corr" data-id="${rec.id}" style="background: none; border: 1px solid var(--status-danger-solid); color: var(--status-danger-solid); padding: 4px 12px; border-radius: 4px; cursor: pointer; font-size: 0.75rem;">Solicitar Correção</button>
            ` : ''}
            ${hasValidationPending ? `
              <button class="btn-validate-corr" data-corrid="${correction?.id}" style="background: var(--status-warning-bg); border: 1px solid var(--status-warning-border); color: var(--status-warning-text); padding: 4px 12px; border-radius: 4px; cursor: pointer; font-size: 0.75rem; font-weight: 600;">Validar Correção</button>
            ` : ''}
            ${hasPendingCorrection ? `
              <span style="font-size: 0.75rem; color: var(--status-danger-solid); font-weight: 600;">Aguardando Inspetor</span>
            ` : ''}
          </div>
        </div>
      </div>
    `;
  }

  // Bloco 8, Parte B: the previous version paired every background with a
  // hardcoded `color: white` at the call site. `--color-primary`,
  // `--status-warning-solid` and `--status-success-solid` all resolve to
  // the same gold (#C7A75B, ~2.1:1 contrast with white — fails WCAG AA),
  // and `--status-danger-solid` resolves to pure white (white text on a
  // white badge, fully invisible). Returning a matched {bg, text} pair per
  // status fixes the contrast for real instead of only changing which
  // token gets misused, and also makes EM ANÁLISE/AGUARDANDO
  // VALIDAÇÃO/CONFORME visually distinct from each other again (all three
  // rendered identically gold before).
  private getMinutaStatusColors(status: string): { bg: string; text: string } {
    switch (status) {
      case 'AGUARDANDO ANÁLISE': return { bg: 'var(--color-graphite-500)', text: '#FFFFFF' };
      case 'EM ANÁLISE': return { bg: 'var(--color-primary)', text: 'var(--color-primary-text)' };
      case 'CORREÇÃO NECESSÁRIA':
      case 'AGUARDANDO INSPETOR': return { bg: '#7f1d1d', text: '#FFFFFF' };
      case 'AGUARDANDO VALIDAÇÃO': return { bg: 'var(--status-warning-solid)', text: 'var(--color-primary-text)' };
      case 'CONFORME':
      case 'ANÁLISE CONCLUÍDA': return { bg: 'var(--status-success-solid)', text: 'var(--color-primary-text)' };
      case 'APROVADA': return { bg: '#10b981', text: 'var(--color-primary-text)' };
      default: return { bg: 'var(--color-graphite-500)', text: '#FFFFFF' };
    }
  }

  // Display-only label (Fase 6/11): never expose raw internal status values,
  // and show the final state as "APROVADA E ASSINADA" without renaming the
  // underlying MinutaStatus domain value ('APROVADA').
  private getMinutaStatusLabel(status: string): string {
    if (status === 'CONFORME' || status === 'ANÁLISE CONCLUÍDA') return 'MINUTA VALIDADA';
    if (status === 'ATUALIZAÇÃO PENDENTE' || status === 'EMITIDA') return 'MINUTA PENDENTE';
    if (status === 'APROVADA') return 'APROVADA E ASSINADA';
    if (status === 'ANÁLISE CONCLUÍDA') return 'CONFORME'; // legado, tratado como sinônimo
    return status;
  }

  private renderModals() {
    const report = this.workspaceData?.report as any;
    return `
      ${this.showHistoryModal ? this.renderMinutaHistoryModal() : ''}
      ${this.showEditReportModal && report ? `
        <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 100; display: flex; justify-content: center; align-items: center;">
          <div style="background: white; padding: 24px; border-radius: 8px; width: 640px; max-height: 85vh; overflow-y: auto; color: black;">
            <h3 style="margin-top: 0;">Editar Relatório</h3>
            <div style="background: #fef3c7; border: 1px solid #fde68a; border-radius: 6px; padding: 10px 12px; margin-bottom: 12px; font-size: 0.8125rem; color: #92400e;">
              Somente campos exclusivos do RI podem ser editados aqui. TAG/Projeto/Unidade-Área vêm do Equipamento/Projeto; Recommendations vêm do Checklist — edite-as na aba "Análise da Minuta" ou "Checklist & Fotos".
            </div>
            <label style="display:block; font-size:0.8rem; font-weight:600; margin: 8px 0 4px;">Objetivo</label>
            <textarea id="edit-ri-objective" style="width: 100%; height: 60px; margin: 0 0 8px; border: 1px solid #ccc; border-radius: 4px; padding: 8px; font-family: inherit; box-sizing: border-box;">${report.objective || ''}</textarea>
            <label style="display:block; font-size:0.8rem; font-weight:600; margin: 8px 0 4px;">Documentos de Referência</label>
            <textarea id="edit-ri-refdocs" style="width: 100%; height: 60px; margin: 0 0 8px; border: 1px solid #ccc; border-radius: 4px; padding: 8px; font-family: inherit; box-sizing: border-box;">${report.referenceDocuments || ''}</textarea>
            <label style="display:block; font-size:0.8rem; font-weight:600; margin: 8px 0 4px;">Observações da Inspeção</label>
            <textarea id="edit-ri-obs" style="width: 100%; height: 60px; margin: 0 0 8px; border: 1px solid #ccc; border-radius: 4px; padding: 8px; font-family: inherit; box-sizing: border-box;">${report.inspectionObservations || ''}</textarea>
            <label style="display:block; font-size:0.8rem; font-weight:600; margin: 8px 0 4px;">Serviços de Manutenção</label>
            <textarea id="edit-ri-maint" style="width: 100%; height: 60px; margin: 0 0 8px; border: 1px solid #ccc; border-radius: 4px; padding: 8px; font-family: inherit; box-sizing: border-box;">${report.maintenanceServices || ''}</textarea>
            <label style="display:block; font-size:0.8rem; font-weight:600; margin: 8px 0 4px;">Datas Limites para Próximas Inspeções</label>
            <textarea id="edit-ri-nextdates" style="width: 100%; height: 60px; margin: 0 0 8px; border: 1px solid #ccc; border-radius: 4px; padding: 8px; font-family: inherit; box-sizing: border-box;">${report.nextInspectionDates || ''}</textarea>
            <label style="display:block; font-size:0.8rem; font-weight:600; margin: 8px 0 4px;">Conclusão</label>
            <textarea id="edit-ri-conclusion" style="width: 100%; height: 80px; margin: 0 0 8px; border: 1px solid #ccc; border-radius: 4px; padding: 8px; font-family: inherit; box-sizing: border-box;">${report.conclusion || ''}</textarea>
            <div style="display: flex; justify-content: flex-end; gap: 12px; margin-top: 16px;">
              <button id="btn-cancel-edit-ri" style="padding: 8px 16px; border: 1px solid #ccc; background: white; color: var(--color-text-inverse); cursor: pointer; border-radius: 4px;">Cancelar</button>
              <button id="btn-confirm-edit-ri" style="padding: 8px 16px; background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8); font-weight: 600; border: none; cursor: pointer; border-radius: 4px;">Salvar Alterações</button>
            </div>
          </div>
        </div>
      ` : ''}
      ${this.showReturnMinutaModal && this.minutaContext ? `
        <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 100; display: flex; justify-content: center; align-items: center;">
          <div style="background: white; padding: 24px; border-radius: 8px; width: 500px; color: black;">
            <h3 style="margin-top: 0;">Devolver Minuta para Correção</h3>
            <div style="margin: 12px 0; font-size: 0.875rem;">
              <div><strong>Minuta:</strong> ${this.minutaContext.minuta.trackingNumber}</div>
              <div><strong>Revisão:</strong> ${this.minutaContext.minuta.currentRevision}</div>
            </div>
            <label style="display:block; font-size:0.8rem; font-weight:600; margin: 8px 0 4px;">Motivo *</label>
            <textarea id="return-minuta-reason" style="width: 100%; height: 80px; margin: 0 0 8px; border: 1px solid #ccc; border-radius: 4px; padding: 8px; font-family: inherit; box-sizing: border-box;" placeholder="Descreva o motivo da devolução..."></textarea>
            <label style="display:block; font-size:0.8rem; font-weight:600; margin: 8px 0 4px;">Destino</label>
            <select id="return-minuta-target" style="width: 100%; padding: 8px; border: 1px solid #ccc; border-radius: 4px; box-sizing: border-box;">
              <option value="INSPETOR">Inspetor</option>
              <option value="ELABORADOR">Elaborador / Responsável</option>
              <option value="DOCUMENTACAO">Documentação</option>
            </select>
            <div style="display: flex; justify-content: flex-end; gap: 12px; margin-top: 16px;">
              <button id="btn-cancel-return-minuta" style="padding: 8px 16px; border: 1px solid #ccc; background: white; color: var(--color-text-inverse); cursor: pointer; border-radius: 4px;">Cancelar</button>
              <button id="btn-confirm-return-minuta" style="background: #b91c1c; color: white; border: none; padding: 8px 16px; font-weight: 600; cursor: pointer; border-radius: 4px;">Devolver Minuta</button>
            </div>
          </div>
        </div>
      ` : ''}
      ${this.showStartAnalysisModal ? `
        <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 100; display: flex; justify-content: center; align-items: center;">
          <div style="background: white; padding: 24px; border-radius: 8px; width: 450px; color: black; box-shadow: 0 10px 25px rgba(0,0,0,0.2);">
            <h3 style="margin-top: 0; border-bottom: 1px solid #eee; padding-bottom: 12px; color: var(--color-text-inverse);">INICIAR ANÁLISE DE ENGENHARIA</h3>
            <div style="margin: 16px 0; display: grid; gap: 8px; font-size: 0.9rem;">
              <div><strong>Relatório:</strong> ${this.workspaceData?.report?.id || 'N/A'}</div>
              <div><strong>Status Atual:</strong> ${this.workspaceData?.report ? this.mapStatus(this.workspaceData.report.status) : 'N/A'}</div>
              <div><strong>Responsável:</strong> ${contextStore.get()?.userName}</div>
            </div>
            <div style="display: flex; justify-content: flex-end; gap: 12px; margin-top: 24px;">
              <button id="btn-cancel-start" style="padding: 8px 16px; border: 1px solid #ccc; background: white; cursor: pointer; border-radius: 4px; font-weight: 500;">CANCELAR</button>
              <button id="btn-confirm-start" style="background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8); font-weight: 600; border: none; padding: 8px 16px; cursor: pointer; border-radius: 4px;">Iniciar Análise</button>
            </div>
          </div>
        </div>
      ` : ''}

      ${this.showEditRecModal ? `
        <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 100; display: flex; justify-content: center; align-items: center;">
          <div style="background: white; padding: 24px; border-radius: 8px; width: 640px; max-height: 85vh; overflow-y: auto; color: black;">
            <h3 style="margin-top: 0;">Editar Recomendação</h3>
            <div style="background: var(--status-warning-bg, #fef3c7); border: 1px solid var(--status-warning-border, #fde68a); border-radius: 6px; padding: 10px 12px; margin-bottom: 12px; font-size: 0.8125rem; color: var(--status-warning-text, #92400e);">
              Esta alteração será aplicada ao Checklist de Inspeção e refletirá automaticamente na Minuta — não é um texto independente apenas para o PDF.
            </div>
            <label style="display:block; font-size:0.8rem; font-weight:600; margin: 8px 0 4px;">Descritivo (Escopo) *</label>
            <textarea id="edit-rec-scope" style="width: 100%; height: 90px; margin: 0 0 8px; border: 1px solid #ccc; border-radius: 4px; padding: 8px; font-family: inherit; box-sizing: border-box;" placeholder="Escopo da recomendação..."></textarea>

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
              <div>
                <label style="display:block; font-size:0.8rem; font-weight:600; margin: 8px 0 4px;">Localização</label>
                <input id="edit-rec-location" type="text" style="width: 100%; border: 1px solid #ccc; border-radius: 4px; padding: 8px; box-sizing: border-box;">
              </div>
              <div>
                <label style="display:block; font-size:0.8rem; font-weight:600; margin: 8px 0 4px;">Dimensão</label>
                <input id="edit-rec-dimension" type="text" style="width: 100%; border: 1px solid #ccc; border-radius: 4px; padding: 8px; box-sizing: border-box;">
              </div>
              <div>
                <label style="display:block; font-size:0.8rem; font-weight:600; margin: 8px 0 4px;">Disciplina</label>
                <select id="edit-rec-discipline" style="width: 100%; border: 1px solid #ccc; border-radius: 4px; padding: 8px; box-sizing: border-box;">
                  <option value="CALDEIRARIA">CALDEIRARIA</option>
                  <option value="SOLDA">SOLDA</option>
                  <option value="PINTURA">PINTURA</option>
                  <option value="ISOLAMENTO">ISOLAMENTO</option>
                  <option value="INSPEÇÃO">INSPEÇÃO</option>
                  <option value="OPERAÇÃO">OPERAÇÃO</option>
                </select>
              </div>
              <div>
                <label style="display:block; font-size:0.8rem; font-weight:600; margin: 8px 0 4px;">Criticidade</label>
                <select id="edit-rec-criticality" style="width: 100%; border: 1px solid #ccc; border-radius: 4px; padding: 8px; box-sizing: border-box;">
                  <option value="LOW">BAIXA</option>
                  <option value="MODERATE">MODERADA</option>
                  <option value="HIGH">ALTA</option>
                  <option value="CRITICAL">CRÍTICA</option>
                </select>
              </div>
              <div>
                <label style="display:block; font-size:0.8rem; font-weight:600; margin: 8px 0 4px;">Execução</label>
                <select id="edit-rec-execute" style="width: 100%; border: 1px solid #ccc; border-radius: 4px; padding: 8px; box-sizing: border-box;">
                  <option value="ROUTINE">ROTINA</option>
                  <option value="SHUTDOWN">PARADA (SHUTDOWN)</option>
                </select>
              </div>
            </div>

            <label style="display:block; font-size:0.8rem; font-weight:600; margin: 8px 0 4px;">Dados Técnicos</label>
            <textarea id="edit-rec-technical" style="width: 100%; height: 50px; margin: 0 0 8px; border: 1px solid #ccc; border-radius: 4px; padding: 8px; font-family: inherit; box-sizing: border-box;"></textarea>

            <label style="display:block; font-size:0.8rem; font-weight:600; margin: 8px 0 4px;">Observações</label>
            <textarea id="edit-rec-observations" style="width: 100%; height: 50px; margin: 0 0 8px; border: 1px solid #ccc; border-radius: 4px; padding: 8px; font-family: inherit; box-sizing: border-box;"></textarea>

            <div style="display: flex; justify-content: flex-end; gap: 12px; margin-top: 16px;">
              <button id="btn-cancel-edit" style="padding: 8px 16px; border: 1px solid #ccc; background: white; cursor: pointer; border-radius: 4px;">Cancelar</button>
              <button id="btn-confirm-edit" style="padding: 8px 16px; background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8); font-weight: 600; border: none; cursor: pointer; border-radius: 4px;">Salvar Alterações</button>
            </div>
          </div>
        </div>
      ` : ''}

      ${this.showRequestCorrectionModal ? `
        <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 100; display: flex; justify-content: center; align-items: center;">
          <div style="background: white; padding: 24px; border-radius: 8px; width: 500px; color: black;">
            <h3 style="margin-top: 0;">Solicitar Correção ao Inspetor</h3>
            <textarea id="req-corr-reason" style="width: 100%; height: 80px; margin: 12px 0; border: 1px solid #ccc; border-radius: 4px; padding: 8px; font-family: inherit;" placeholder="Descreva o motivo da correção e instruções para o inspetor..."></textarea>
            <div style="display: flex; justify-content: flex-end; gap: 12px; margin-top: 24px;">
              <button id="btn-cancel-req" style="padding: 8px 16px; border: 1px solid #ccc; background: white; cursor: pointer; border-radius: 4px;">Cancelar</button>
              <button id="btn-confirm-req" style="background: #b91c1c; color: white; border: none; padding: 8px 16px; cursor: pointer; border-radius: 4px; font-weight: 600;">Enviar Solicitação</button>
            </div>
          </div>
        </div>
      ` : ''}
      
      ${this.showValidateCorrectionModal ? `
        <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 100; display: flex; justify-content: center; align-items: center;">
          <div style="background: white; padding: 24px; border-radius: 8px; width: 600px; color: black;">
            <h3 style="margin-top: 0;">Validar Correção</h3>
            <p>O inspetor de campo devolveu a correção.</p>
            <div style="display: flex; justify-content: flex-end; gap: 12px; margin-top: 24px;">
              <button id="btn-return-val" style="padding: 8px 16px; color: #b91c1c; border: 1px solid #b91c1c; background: white; cursor: pointer; border-radius: 4px;">Devolver novamente</button>
              <button id="btn-cancel-val" style="padding: 8px 16px; border: 1px solid #ccc; background: white; cursor: pointer; border-radius: 4px;">Cancelar</button>
              <button id="btn-confirm-val" style="background: var(--status-success-solid); color: var(--color-primary-text); border: none; padding: 8px 16px; cursor: pointer; border-radius: 4px; font-weight: 600;">Aprovar Correção</button>
            </div>
          </div>
        </div>
      ` : ''}
    `;
  }

  private async handleStartAnalysis() {
    this.showStartAnalysisModal = true;
    this.render();
  }

  private attachEvents() {
    this.querySelector("#btn-back")?.addEventListener("click", () => router.navigate("/engineering"));

    this.querySelector("#btn-start")?.addEventListener("click", () => this.handleStartAnalysis());

    const pdfPreviewEl = this.querySelector("#minuta-pdf-preview") as any;
    if (pdfPreviewEl && this.minutaPdfSnapshot) {
      pdfPreviewEl.snapshot = this.minutaPdfSnapshot;
    }
    const riPdfPreviewEl = this.querySelector("#ri-pdf-preview") as any;
    if (riPdfPreviewEl && this.riPdfSnapshot) {
      riPdfPreviewEl.snapshot = this.riPdfSnapshot;
    }

    this.querySelector("#btn-ri-validate")?.addEventListener("click", async () => {
      if (confirm("Confirma a validação do Relatório de Integridade como CONFORME? Ele ficará pronto para aprovação e assinatura do PLH.")) {
        try {
          const c = contextStore.get();
          const reqContext = { activeClientId: c.activeClientId || "", permissions: c.permissions, correlationId: "", userId: c.userId, professionalId: c.professionalId || null } as any;
          await platform.engineeringService.validateReport(reqContext, this.reportId as any);
          this.workspaceData = await platform.engineeringService.getWorkspaceContext(reqContext, this.reportId as any);
          await this.loadRiPdfSnapshot(reqContext);
          this.render();
        } catch (e) {
          alert(e instanceof Error ? e.message : String(e));
        }
      }
    });
    this.querySelector("#btn-ri-approve")?.addEventListener("click", async () => {
      if (confirm("Confirma aprovação técnica e assinatura deste Relatório de Integridade como PLH?")) {
        try {
          const c = contextStore.get();
          const reqContext = { activeClientId: c.activeClientId || "", permissions: c.permissions, correlationId: "", userId: c.userId, professionalId: c.professionalId || null } as any;
          await platform.engineeringService.approveTechnical(reqContext, this.reportId as any, "engenheiro@inspect.ai");
          this.workspaceData = await platform.engineeringService.getWorkspaceContext(reqContext, this.reportId as any);
          await this.loadRiPdfSnapshot(reqContext);
          this.render();
          alert("Relatório aprovado e assinado com sucesso!");
        } catch (e) {
          alert(e instanceof Error ? e.message : String(e));
        }
      }
    });
    this.querySelector("#btn-ri-edit")?.addEventListener("click", () => {
      this.showEditReportModal = true;
      this.render();
    });
    this.querySelector("#btn-cancel-edit-ri")?.addEventListener("click", () => {
      this.showEditReportModal = false;
      this.render();
    });
    this.querySelector("#btn-confirm-edit-ri")?.addEventListener("click", async () => {
      const val = (id: string) => (this.querySelector(id) as HTMLTextAreaElement)?.value ?? '';
      try {
        const c = contextStore.get();
        const reqContext = { activeClientId: c.activeClientId || "", permissions: c.permissions, correlationId: "", userId: c.userId } as any;
        await platform.engineeringService.editReportFields(reqContext, this.reportId as any, {
          objective: val('#edit-ri-objective'),
          referenceDocuments: val('#edit-ri-refdocs'),
          inspectionObservations: val('#edit-ri-obs'),
          maintenanceServices: val('#edit-ri-maint'),
          nextInspectionDates: val('#edit-ri-nextdates'),
          conclusion: val('#edit-ri-conclusion')
        });
        this.showEditReportModal = false;
        this.workspaceData = await platform.engineeringService.getWorkspaceContext(reqContext, this.reportId as any);
        await this.loadRiPdfSnapshot(reqContext);
        this.render();
      } catch (e) {
        alert(e instanceof Error ? e.message : String(e));
      }
    });

    this.querySelector("#btn-minuta-return")?.addEventListener("click", () => {
      this.showReturnMinutaModal = true;
      this.render();
    });
    this.querySelector("#btn-cancel-return-minuta")?.addEventListener("click", () => {
      this.showReturnMinutaModal = false;
      this.render();
    });
    this.querySelector("#btn-confirm-return-minuta")?.addEventListener("click", async () => {
      const reason = (this.querySelector("#return-minuta-reason") as HTMLTextAreaElement)?.value;
      const target = (this.querySelector("#return-minuta-target") as HTMLSelectElement)?.value || 'INSPETOR';
      if (!reason || !reason.trim()) return alert("Motivo obrigatório.");
      try {
        const c = contextStore.get();
        const reqContext = { activeClientId: c.activeClientId || "", permissions: c.permissions, correlationId: "", userId: c.userId } as any;
        await platform.engineeringService.returnMinutaForCorrection(reqContext, this.minutaContext!.minuta.id, reason, target);
        this.showReturnMinutaModal = false;
        await this.loadMinutaContext();
      } catch (e) {
        alert(e instanceof Error ? e.message : String(e));
      }
    });

    this.querySelectorAll(".workspace-tab").forEach(tab => {
      tab.addEventListener("click", async (e) => {
        this.activeTab = (e.currentTarget as HTMLElement).getAttribute("data-tab") || "summary";
        if (this.activeTab === 'minuta' && !this.minutaContext) {
          await this.loadMinutaContext();
        } else if (this.activeTab === 'ndt' && !this.ndtLoaded) {
          this.render();
          await this.loadNdtRows();
          this.render();
        } else {
          this.render();
        }
      });
    });

    this.querySelector("#btn-minuta-start")?.addEventListener("click", () => {
      this.showStartAnalysisModal = true;
      this.render();
    });
    this.querySelector("#btn-minuta-validate")?.addEventListener("click", async () => {
      const ctx = contextStore.get();
      if (!ctx?.permissions.has(PermissionCode.EngineeringValidateMinuta)) {
         alert("Você não possui permissão (engineering.validate_minuta) para validar a Minuta.");
         return;
      }
      if (confirm("Confirma a validação da Minuta como CONFORME? Ela ficará pronta para aprovação e assinatura do PLH.")) {
        try {
          const ctx = contextStore.get();
          const reqContext = { activeClientId: ctx.activeClientId || "", permissions: ctx.permissions, correlationId: "", userId: ctx.userId, professionalId: ctx.professionalId || null } as any;
          await platform.engineeringService.validateMinuta(reqContext, this.minutaContext!.minuta.id);
          await this.loadMinutaContext();
        } catch (e) {
          alert(e instanceof Error ? e.message : String(e));
        }
      }
    });
    this.querySelector("#btn-minuta-edit-mode")?.addEventListener("click", () => {
      const ctx = contextStore.get();
      if (!ctx?.permissions.has(PermissionCode.EngineeringEditRecommendation)) {
        alert("Você não possui permissão (engineering.edit_recommendation) para editar a Minuta.");
        return;
      }
      this.minutaEditMode = true;
      this.render();
      this.querySelector('#minuta-recommendations-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    this.querySelector("#btn-minuta-edit-mode-exit")?.addEventListener("click", () => {
      this.minutaEditMode = false;
      this.render();
    });
    this.querySelector("#btn-minuta-goto-correction")?.addEventListener("click", () => {
      this.querySelector('#minuta-recommendations-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    this.querySelector("#btn-minuta-history")?.addEventListener("click", () => {
      this.showHistoryModal = true;
      this.render();
    });
    this.querySelector("#btn-close-minuta-history")?.addEventListener("click", () => {
      this.showHistoryModal = false;
      this.render();
    });
    this.querySelector("#btn-minuta-approve")?.addEventListener("click", async () => {
      const ctx = contextStore.get();
      if (!ctx?.permissions.has(PermissionCode.EngineeringApproveMinuta) || !ctx?.permissions.has(PermissionCode.EngineeringSignMinuta)) {
         alert("Permissão negada. É necessário 'engineering.approve_minuta' e 'engineering.sign_minuta' para aprovar e assinar.");
         return;
      }
      if (confirm("Confirma aprovação da Minuta? Este processo registrará sua assinatura digital e congelará o documento.")) {
        try {
          const ctx = contextStore.get();
          const reqContext = { activeClientId: ctx.activeClientId || "", permissions: ctx.permissions, correlationId: "", userId: ctx.userId, professionalId: ctx.professionalId || null } as any;
          await platform.engineeringService.approveAndSignMinuta(reqContext, this.minutaContext!.minuta.id);
          await this.loadMinutaContext();
          alert("Minuta aprovada e assinada digitalmente com sucesso!");
        } catch (e) {
          alert(e instanceof Error ? e.message : String(e));
        }
      }
    });

    // EDITAR Nº ZR — ação de Engenharia sobre a numeração da Recommendation.
    this.querySelectorAll(".btn-edit-zr").forEach(btn => {
      btn.addEventListener("click", async (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute("data-id");
        const rec = this.minutaContext?.recommendations.find(r => r.id === id);
        if (!id || !rec) return;

        const newZr = prompt(
          `EDITAR NUMERAÇÃO ZR\n\nRecomendação: ${rec.recommendationNumber || rec.code || rec.id}\nNúmero ZR atual: ${formatZrDisplay(rec)}\n\nNovo número ZR:`,
          rec.zr || ""
        );
        if (newZr === null) return;

        const reason = prompt("Motivo da alteração (obrigatório):", "");
        if (reason === null) return;

        try {
          await platform.reportService.updateRecommendationZrNumber(this.reqCtx(), id as any, {
            newZr,
            reason: reason || ""
          });
          await this.loadWorkspace();
        } catch (err: any) {
          console.error("[ZR] Falha ao editar a numeração", err);
          alert(err?.safeMessage || err?.message || "Não foi possível alterar a numeração ZR.");
        }
      });
    });

    // REMOVER Nº ZR — a Recommendation permanece e volta para a fila.
    this.querySelectorAll(".btn-remove-zr").forEach(btn => {
      btn.addEventListener("click", async (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute("data-id");
        const rec = this.minutaContext?.recommendations.find(r => r.id === id);
        if (!id || !rec) return;

        const confirmed = confirm(
          `REMOVER NUMERAÇÃO ZR?\n\nRecomendação: ${rec.recommendationNumber || rec.code || rec.id}\nNúmero atual: ${formatZrDisplay(rec)}\n\nA Recommendation permanecerá cadastrada e retornará para a fila de pendência de numeração ZR.`
        );
        if (!confirmed) return;

        const reason = prompt("Motivo da remoção (obrigatório):", "");
        if (reason === null) return;

        try {
          await platform.reportService.removeRecommendationZrNumber(this.reqCtx(), id as any, {
            reason: reason || ""
          });
          await this.loadWorkspace();
        } catch (err: any) {
          console.error("[ZR] Falha ao remover a numeração", err);
          alert(err?.safeMessage || err?.message || "Não foi possível remover a numeração ZR.");
        }
      });
    });

    this.querySelectorAll(".btn-edit-rec").forEach(btn => {
      btn.addEventListener("click", (e) => {
        const ctx = contextStore.get();
        if (this.workspaceData?.report?.status !== 'UNDER_ENGINEERING_REVIEW') {
           alert("A análise de Engenharia ainda não foi iniciada. Inicie a análise para editar.");
           return;
        }
        if (!ctx?.permissions.has(PermissionCode.EngineeringEditRecommendation)) {
           alert("Você não possui permissão (engineering.edit_recommendation) para editar.");
           return;
        }

        this.activeRecId = (e.currentTarget as HTMLElement).getAttribute("data-id");
        this.showEditRecModal = true;

        const rec = this.minutaContext?.recommendations.find(r => r.id === this.activeRecId);
        setTimeout(() => {
          if (!rec) return;
          const scopeEl = this.querySelector("#edit-rec-scope") as HTMLTextAreaElement;
          const locationEl = this.querySelector("#edit-rec-location") as HTMLInputElement;
          const dimensionEl = this.querySelector("#edit-rec-dimension") as HTMLInputElement;
          const disciplineEl = this.querySelector("#edit-rec-discipline") as HTMLSelectElement;
          const criticalityEl = this.querySelector("#edit-rec-criticality") as HTMLSelectElement;
          const executeEl = this.querySelector("#edit-rec-execute") as HTMLSelectElement;
          const technicalEl = this.querySelector("#edit-rec-technical") as HTMLTextAreaElement;
          const observationsEl = this.querySelector("#edit-rec-observations") as HTMLTextAreaElement;
          if (scopeEl) scopeEl.value = rec.scope || '';
          if (locationEl) locationEl.value = rec.location || '';
          if (dimensionEl) dimensionEl.value = rec.dimension || '';
          if (disciplineEl && rec.discipline) disciplineEl.value = rec.discipline;
          if (criticalityEl && rec.criticality) criticalityEl.value = rec.criticality;
          if (executeEl && rec.executeIn) executeEl.value = rec.executeIn;
          if (technicalEl) technicalEl.value = rec.technicalData || '';
          if (observationsEl) observationsEl.value = rec.observations || '';
        }, 10);

        this.render();
      });
    });

    this.querySelectorAll(".btn-req-corr").forEach(btn => {
      btn.addEventListener("click", (e) => {
        const ctx = contextStore.get();
        if (this.workspaceData?.report?.status !== 'UNDER_ENGINEERING_REVIEW') {
           alert("A análise de Engenharia ainda não foi iniciada. Inicie a análise para solicitar correções.");
           return;
        }
        if (!ctx?.permissions.has(PermissionCode.EngineeringRequestCorrection)) {
           alert("Você não possui permissão (engineering.request_correction) para solicitar correções.");
           return;
        }

        this.activeRecId = (e.currentTarget as HTMLElement).getAttribute("data-id");
        this.showRequestCorrectionModal = true;
        this.render();
      });
    });

    this.querySelectorAll(".btn-validate-corr").forEach(btn => {
      btn.addEventListener("click", (e) => {
        const ctx = contextStore.get();
        if (this.workspaceData?.report?.status !== 'UNDER_ENGINEERING_REVIEW') {
           alert("A análise de Engenharia ainda não foi iniciada.");
           return;
        }
        if (!ctx?.permissions.has(PermissionCode.EngineeringValidateCorrection)) {
           alert("Você não possui permissão para validar correções.");
           return;
        }

        this.activeCorrectionId = (e.currentTarget as HTMLElement).getAttribute("data-corrid");
        this.showValidateCorrectionModal = true;
        this.render();
      });
    });

    this.querySelectorAll(".btn-validate-item").forEach(btn => {
      btn.addEventListener("click", async (e) => {
        const recId = (e.currentTarget as HTMLElement).getAttribute("data-recid");
        if (!recId || !this.minutaContext) return;
        if (confirm("Confirma que a constatação, o comentário técnico e a Recommendation deste item estão tecnicamente adequados?")) {
          try {
            const ctx = contextStore.get();
            const reqContext = { activeClientId: ctx.activeClientId || "", permissions: ctx.permissions, correlationId: "", userId: ctx.userId } as any;
            await platform.engineeringService.validateChecklistItem(reqContext, this.minutaContext.minuta.id, recId);
            await this.loadMinutaContext();
          } catch (e) {
            alert(e instanceof Error ? e.message : String(e));
          }
        }
      });
    });

    this.querySelector("#btn-cancel-start")?.addEventListener("click", () => { this.showStartAnalysisModal = false; this.render(); });
    this.querySelector("#btn-confirm-start")?.addEventListener("click", async () => {
      try {
        const ctx = contextStore.get();
        const reqContext = { activeClientId: ctx.activeClientId || "", permissions: ctx.permissions, correlationId: "", userId: ctx.userId } as any;
        await platform.engineeringService.startAnalysis(reqContext, this.workspaceData!.report.id as any);
        if (this.minutaContext) {
          await platform.engineeringService.startMinutaAnalysis(reqContext, this.minutaContext.minuta.id);
        }
        this.showStartAnalysisModal = false;
        
        // Reload report data
        this.workspaceData = await platform.engineeringService.getWorkspaceContext(reqContext, this.workspaceData!.report.id as any);
        if (this.minutaContext) {
          await this.loadMinutaContext();
        } else {
          this.render();
        }
        alert("Análise de Engenharia iniciada com sucesso.");
      } catch (e) {
        alert(e instanceof Error ? e.message : String(e));
      }
    });

    this.querySelector("#btn-cancel-edit")?.addEventListener("click", () => { this.showEditRecModal = false; this.render(); });
    this.querySelector("#btn-confirm-edit")?.addEventListener("click", async () => {
      const scope = (this.querySelector("#edit-rec-scope") as HTMLTextAreaElement)?.value;
      if (!scope) return alert("Escopo não pode ser vazio.");
      const location = (this.querySelector("#edit-rec-location") as HTMLInputElement)?.value;
      const dimension = (this.querySelector("#edit-rec-dimension") as HTMLInputElement)?.value;
      const discipline = (this.querySelector("#edit-rec-discipline") as HTMLSelectElement)?.value;
      const criticality = (this.querySelector("#edit-rec-criticality") as HTMLSelectElement)?.value;
      const executeIn = (this.querySelector("#edit-rec-execute") as HTMLSelectElement)?.value;
      const technicalData = (this.querySelector("#edit-rec-technical") as HTMLTextAreaElement)?.value;
      const observations = (this.querySelector("#edit-rec-observations") as HTMLTextAreaElement)?.value;
      try {
        const ctx = contextStore.get();
        const reqContext = { activeClientId: ctx.activeClientId || "", permissions: ctx.permissions, correlationId: "", userId: ctx.userId } as any;
        await platform.engineeringService.editRecommendation(reqContext, this.minutaContext!.minuta.id, this.activeRecId!, {
          scope, location, dimension, discipline, criticality, executeIn, technicalData, observations
        } as any);
        this.showEditRecModal = false;
        await this.loadMinutaContext();
      } catch (e) {
        alert(e instanceof Error ? e.message : String(e));
      }
    });

    this.querySelector("#btn-cancel-req")?.addEventListener("click", () => { this.showRequestCorrectionModal = false; this.render(); });
    this.querySelector("#btn-confirm-req")?.addEventListener("click", async () => {
      const reason = (this.querySelector("#req-corr-reason") as HTMLTextAreaElement)?.value;
      if (!reason) return alert("Motivo obrigatório.");
      try {
        const ctx = contextStore.get();
        const reqContext = { activeClientId: ctx.activeClientId || "", permissions: ctx.permissions, correlationId: "", userId: ctx.userId } as any;
        const targetRec = this.minutaContext?.recommendations.find(r => r.id === this.activeRecId);
        const visualIndex = targetRec?.recommendationNumber || targetRec?.code || targetRec?.zr || this.activeRecId!;
        await platform.engineeringService.requestCorrectionToInspector(reqContext, this.minutaContext!.minuta.id, this.activeRecId!, visualIndex, reason);
        this.showRequestCorrectionModal = false;
        await this.loadMinutaContext();
      } catch (e) {
        alert(e instanceof Error ? e.message : String(e));
      }
    });

    this.querySelector("#btn-cancel-val")?.addEventListener("click", () => { this.showValidateCorrectionModal = false; this.render(); });
    this.querySelector("#btn-confirm-val")?.addEventListener("click", async () => {
      try {
        const ctx = contextStore.get();
        const reqContext = { activeClientId: ctx.activeClientId || "", permissions: ctx.permissions, correlationId: "", userId: ctx.userId } as any;
        await platform.engineeringService.validateCorrection(reqContext, this.minutaContext!.minuta.id, this.activeCorrectionId!);
        this.showValidateCorrectionModal = false;
        await this.loadMinutaContext();
      } catch (e) {
        alert(e instanceof Error ? e.message : String(e));
      }
    });
    this.querySelector("#btn-return-val")?.addEventListener("click", async () => {
      const r = prompt("Motivo da devolução ao inspetor:");
      if (!r) return;
      try {
        const ctx = contextStore.get();
        const reqContext = { activeClientId: ctx.activeClientId || "", permissions: ctx.permissions, correlationId: "", userId: ctx.userId } as any;
        await platform.engineeringService.returnCorrectionToInspector(reqContext, this.minutaContext!.minuta.id, this.activeCorrectionId!, r);
        this.showValidateCorrectionModal = false;
        await this.loadMinutaContext();
      } catch (e) {
        alert(e instanceof Error ? e.message : String(e));
      }
    });

    this.querySelector("#btn-return")?.addEventListener("click", () => this.openReturnModal());
    this.querySelector("#btn-finalize-report")?.addEventListener("click", () => void this.onFinalizeReport());
    this.querySelector("#btn-update-minuta")?.addEventListener("click", () => void this.onUpdateMinuta());
    this.querySelector("#btn-minuta-save-draft")?.addEventListener("click", () => void this.onSaveMinutaDraft());
    this.querySelector("#minuta-notes")?.addEventListener("input", e => { this.minutaNotes = (e.target as HTMLTextAreaElement).value; });
    this.querySelector("#btn-minuta-validate-head")?.addEventListener("click", () => void this.onValidateMinutaTecnica());
    this.querySelector("#btn-minuta-request-correction")?.addEventListener("click", () => this.openMinutaCorrectionModal());
    this.querySelector("#btn-minuta-preview")?.addEventListener("click", () => { this.minutaPreviewOpen = !this.minutaPreviewOpen; this.render(); const el = this.querySelector("#minuta-pdf-preview-head") as any; if (el && this.minutaPdfSnapshot) el.snapshot = this.minutaPdfSnapshot; });
    this.querySelector("#btn-minuta-history-head")?.addEventListener("click", () => { this.showHistoryModal = true; this.render(); });
    this.querySelector("#btn-minuta-edit-mode-head")?.addEventListener("click", () => { this.minutaEditMode = !this.minutaEditMode; this.render(); this.querySelector('#minuta-recommendations-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); });
    this.querySelectorAll(".btn-ndt-route").forEach(b => b.addEventListener("click", () => { const route = (b as HTMLElement).dataset["route"]; if (route) router.navigate(route); }));
    this.querySelector("#assess-condition")?.addEventListener("change", e => { this.assessmentForm.condition = (e.target as HTMLSelectElement).value as EquipmentCondition | ""; this.render(); });
    this.querySelector("#assess-months")?.addEventListener("input", e => { this.assessmentForm.months = (e.target as HTMLInputElement).value; });
    this.querySelector("#btn-save-assessment")?.addEventListener("click", () => void this.onSaveAssessment());
    this.querySelector("#btn-generate-minuta")?.addEventListener("click", () => void this.onGenerateMinuta());
    this.querySelector("#btn-open-minuta")?.addEventListener("click", async () => { this.activeTab = "minuta"; if (!this.minutaContext) await this.loadMinutaContext(); else this.render(); });
    this.querySelector("#btn-open-avr")?.addEventListener("click", e => { const id = (e.currentTarget as HTMLElement).dataset["avr"]; if (id) router.navigate(`/integrity/avr/${id}`); });

    this.querySelector("#btn-review-release")?.addEventListener("click", async () => {
      if (confirm("Deseja revisar a liberação? O laudo voltará para 'Em Análise' e poderá ser devolvido ou aprovado novamente.")) {
        if (!this.reportId) return;
        try {
          const ctx = contextStore.get();
          const reqContext = { activeClientId: ctx.activeClientId || "", permissions: ctx.permissions, correlationId: "", userId: ctx.userId } as any;
          const updated = await platform.engineeringService.reopenForReview(reqContext, this.reportId as any);
          if (this.workspaceData) this.workspaceData.report = updated;
          this.render();
          alert("O laudo voltou para 'Em Análise'.");
        } catch (e) {
          alert("Erro ao revisar liberação: " + (e instanceof Error ? e.message : String(e)));
        }
      }
    });

    this.querySelector("#btn-approve")?.addEventListener("click", async () => {
      const pendingCount = this.checklistComponents.filter(c => c.result === 'PENDING' || c.status === 'PENDING').length;
      if (pendingCount > 0) {
        alert(`Não é possível aprovar este laudo. Existem ${pendingCount} componente(s) com pendências de preenchimento no Checklist Técnico de campo.`);
        return;
      }

      if (confirm("Confirma aprovação técnica deste laudo? Um e-mail será enviado para sua assinatura digital.")) {
        if (!this.reportId) return;
        try {
          const ctx = contextStore.get();
          const reqContext = { activeClientId: ctx.activeClientId || "", permissions: ctx.permissions, correlationId: "", userId: ctx.userId } as any;
          // In real life get email from user profile
          const updated = await platform.engineeringService.approveTechnical(reqContext, this.reportId as any, "engenheiro@inspect.ai");
          if (this.workspaceData) this.workspaceData.report = updated;
          this.render();
          alert("Aprovado! E-mail de assinatura enviado.");
        } catch (e) {
          alert("Erro ao aprovar: " + (e instanceof Error ? e.message : String(e)));
        }
      }
    });
  }
}

if (!customElements.get("app-engineering-workspace")) {
  customElements.define("app-engineering-workspace", EngineeringWorkspace);
}




